import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import axios from 'axios';

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

import Layout from '../components/Layout';
import PatientForm from '../components/PatientForm';
import PatientStatusAction, { PatientStatusFilter, PatientInactiveNotice, matchesPatientStatus } from '../components/PatientStatusControls';

import ImageModal from '../components/ImageModal';

import OverviewTab from '../components/OverviewTab';
import TreatmentsTab from '../components/TreatmentsTab';
import AlignersTab from '../components/AlignersTab';
import AppointmentsTab from '../components/AppointmentsTab';
import ContextualAIHistory from '../components/ai/ContextualAIHistory';
import ImagesTab from '../components/ImagesTab';
import PatientDocumentsTab from '../components/PatientDocumentsTab';
// ALTERADO: usamos o novo componente de anamnese no prontuário
import PatientAnamneseTab from '../components/PatientAnamneseTab';
import FinancialTab from '../components/FinancialTab';
import EvolutionTab from '../components/EvolutionTab'; // NOVO: Aba de Evolução Clínica
import OdontogramFacesEmbed from '../components/OdontogramFacesEmbed';

const CHECK_IN_ALLOWED_STATUSES = new Set(['Agendado', 'Confirmado']);
const canStartAppointment = (appointment) =>
  CHECK_IN_ALLOWED_STATUSES.has(appointment?.status);

import { createContract, listContractTemplates } from '../services/contractsApi';

import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

import {
  FileText,
  Users,
  Brain,
  Image as ImageIcon,
  Smile,
  Plus,
  Edit,
  Calendar,
  Activity,
  DollarSign,
  Grid,
  Eye,
  Search,
  ArrowLeft,
  MessageCircle,
  ShieldCheck,
  ClipboardList,
  FileSignature,
  FileCheck2,
  History,
  PenLine,
  FilePlus2,
  Camera,
  Upload,
  Link as LinkIcon,
  UserRound,
  XCircle,
  RefreshCw,
  ChevronDown,
  GitMerge
} from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const onlyDigits = (value) => String(value || '').replace(/\D+/g, '');

const formatCpfDisplay = (value) => {
  const v = onlyDigits(value).slice(0, 11);
  if (v.length !== 11) return value || '—';
  return `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
};

const formatPhoneDisplay = (value) => {
  const v = onlyDigits(value).slice(0, 11);
  if (v.length === 10) return `(${v.slice(0, 2)}) ${v.slice(2, 6)}-${v.slice(6)}`;
  if (v.length === 11) return `(${v.slice(0, 2)}) ${v.slice(2, 7)}-${v.slice(7)}`;
  return value || '—';
};

function getQS(name) {
  if (typeof window === 'undefined') return null;
  const url = new URL(window.location.href);
  return url.searchParams.get(name);
}

function normalizeDashboardStatus(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();
}

function safeDashboardDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizePatientGender(value) {
  const v = String(value || '').trim().toLowerCase();

  if (['f', 'feminino', 'female'].includes(v)) return 'Feminino';
  if (['m', 'masculino', 'male'].includes(v)) return 'Masculino';
  if (['o', 'outro', 'other'].includes(v)) return 'Outro';

  return value || '';
}

function normalizePatientMaritalStatus(value) {
  const v = String(value || '').trim().toLowerCase();

  if (['solteiro', 'solteira', 'solteiro(a)', 'single'].includes(v)) return 'Solteiro(a)';
  if (['casado', 'casada', 'casado(a)', 'married'].includes(v)) return 'Casado(a)';
  if (['divorciado', 'divorciada', 'divorciado(a)', 'divorced'].includes(v)) return 'Divorciado(a)';
  if (['viuvo', 'viúva', 'viúvo', 'viuvo(a)', 'viúvo(a)', 'widowed'].includes(v)) return 'Viúvo(a)';
  if (['uniao estavel', 'união estável', 'união estavel', 'uniao estável'].includes(v)) return 'União Estável';

  return value || '';
}

function isValidTab(tab) {
  return [
    'overview',
    'timeline',
    'odontogram',
    'treatments',
    'aligners',
    'evolution',
    'appointments',
    'analyses',
    'images',
    'documents',
    'anamnese',
    'financial',
  ].includes(tab);
}

const ADDITIONAL_RECORD_TAB_IDS = new Set([
  'timeline',
  'aligners',
  'appointments',
  'analyses',
  'financial',
]);

// Tradução de códigos técnicos para títulos exibidos ao usuário.
// O valor original permanece preservado no registro de auditoria.
const formatAuditAction = (action) => {
  const titles = {
    BILLING_PAYMENT_DATE_CORRECTED: 'CORRIGIU DATA DE RECEBIMENTO',
  };
  return titles[String(action || '').trim().toUpperCase()] || action || 'Evento';
};

function getTimelineEventMeta(action = '') {
  const text = String(action || '').toLowerCase();

  if (text === 'billing_payment_date_corrected') {
    return {
      label: 'Financeiro',
      icon: DollarSign,
      dot: 'bg-green-600',
      card: 'border-green-200 bg-green-50',
      badge: 'bg-green-100 text-green-700 border-green-200',
    };
  }

  if (text.includes('assinado')) {
    return {
      label: 'Assinatura',
      icon: FileSignature,
      dot: 'bg-emerald-600',
      card: 'border-emerald-200 bg-emerald-50',
      badge: 'bg-emerald-100 text-emerald-700 border-emerald-200',
    };
  }

  if (text.includes('pdf')) {
    return {
      label: 'PDF',
      icon: FileCheck2,
      dot: 'bg-indigo-600',
      card: 'border-indigo-200 bg-indigo-50',
      badge: 'bg-indigo-100 text-indigo-700 border-indigo-200',
    };
  }

  if (text.includes('nova versão')) {
    return {
      label: 'Versão',
      icon: History,
      dot: 'bg-orange-500',
      card: 'border-orange-200 bg-orange-50',
      badge: 'bg-orange-100 text-orange-700 border-orange-200',
    };
  }

  if (text.includes('editado') || text.includes('alterado')) {
    return {
      label: 'Edição',
      icon: PenLine,
      dot: 'bg-blue-600',
      card: 'border-blue-200 bg-blue-50',
      badge: 'bg-blue-100 text-blue-700 border-blue-200',
    };
  }

  if (text.includes('criado')) {
    return {
      label: 'Criação',
      icon: FilePlus2,
      dot: 'bg-purple-600',
      card: 'border-purple-200 bg-purple-50',
      badge: 'bg-purple-100 text-purple-700 border-purple-200',
    };
  }

  if (text.includes('crédito')) {
    return {
      label: 'Financeiro',
      icon: DollarSign,
      dot: 'bg-green-600',
      card: 'border-green-200 bg-green-50',
      badge: 'bg-green-100 text-green-700 border-green-200'
    };
  }

  return {
    label: 'Evento',
    icon: ClipboardList,
    dot: 'bg-slate-500',
    card: 'border-slate-200 bg-slate-50',
    badge: 'bg-slate-100 text-slate-700 border-slate-200',
  };
}

export default function ProntuariosPage() {
  const { user } = useAuth();
  const permissions = useMemo(() => new Set(user?.permissions || []), [user?.permissions]);
  const can = useCallback((permission) => permissions.has(permission), [permissions]);
  const toastCtx = useToast();

  const showSuccess = useCallback(
    (m) => (toastCtx?.showToast ? toastCtx.showToast(m, 'success') : toastCtx?.toast?.success?.(m)),
    [toastCtx]
  );
  const showError = useCallback(
    (m) => (toastCtx?.showToast ? toastCtx.showToast(m, 'error') : toastCtx?.toast?.error?.(m)),
    [toastCtx]
  );
  const showInfo = useCallback(
    (m) => (toastCtx?.showToast ? toastCtx.showToast(m, 'info') : toastCtx?.toast?.info?.(m)),
    [toastCtx]
  );
  const showErrorRef = useRef(showError);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  const forceListMode = getQS('modo') === 'lista';

  const initialPatientId = forceListMode
    ? null
    : getQS('patientId') || getQS('patient_id') || localStorage.getItem('currentPatientId');

  const initialTab = isValidTab(getQS('tab')) ? getQS('tab') : 'overview';

  // ====== Modo tela (lista x detalhe) ======
  const [mode, setMode] = useState(() =>
    initialPatientId ? 'detail' : 'list'
  );

  // ====== Estados gerais ======
  const [loading, setLoading] = useState(true);

  // Pacientes
  const [patients, setPatients] = useState([]);
  const [patientStatus, setPatientStatus] = useState('active');
  const [patientSearch, setPatientSearch] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [mergeSourcePatient, setMergeSourcePatient] = useState(null);
  const [mergeIdentityConfirmed, setMergeIdentityConfirmed] = useState(false);
  const [mergeTargetPatient, setMergeTargetPatient] = useState(null);
  const [mergeTargetSearch, setMergeTargetSearch] = useState('');
  const [mergePreview, setMergePreview] = useState(null);
  const [mergeFieldChoices, setMergeFieldChoices] = useState({});
  const [loadingMergePreview, setLoadingMergePreview] = useState(false);
  const [mergingPatient, setMergingPatient] = useState(false);

  const initialDashboardFilter = getQS('filter') || '';
  const [dashboardFilter, setDashboardFilter] = useState(
    ['inactive', 'stalled'].includes(initialDashboardFilter)
      ? initialDashboardFilter
      : ''
  );
  const [dashboardFilterPatientIds, setDashboardFilterPatientIds] = useState([]);
  const [dashboardFilterDetails, setDashboardFilterDetails] = useState({});
  const [loadingDashboardFilter, setLoadingDashboardFilter] = useState(false);

  // PatientForm (modal)
  const [pfIsOpen, setPfIsOpen] = useState(false);
  const [pfIsEditing, setPfIsEditing] = useState(false);
  const [pfFormData, setPfFormData] = useState({});

  // Abas
  const [activeTab, setActiveTab] = useState(initialTab);
  const [showAdditionalRecordTabs, setShowAdditionalRecordTabs] = useState(
    () => ADDITIONAL_RECORD_TAB_IDS.has(initialTab)
  );

  useEffect(() => {
    if (ADDITIONAL_RECORD_TAB_IDS.has(activeTab)) {
      setShowAdditionalRecordTabs(true);
    }
  }, [activeTab]);

  const [
    focusedEvolutionId,
    setFocusedEvolutionId,
  ] = useState(null);

  const [
    focusedAlignerEvent,
    setFocusedAlignerEvent,
  ] = useState(null);

  useEffect(() => {
    localStorage.setItem("support_active_tab", activeTab);
  }, [activeTab]);

  useEffect(() => {
    const required = {
      timeline: 'audit.view', odontogram: 'clinical_records.view',
      treatments: 'clinical_records.view', evolution: 'clinical_records.view',
      aligners: 'aligners.view', appointments: 'appointments.view',
      analyses: 'ai.clinical_use', images: 'images.view', documents: 'documents.view',
      anamnese: 'anamnese.view', financial: 'billing.view',
    }[activeTab];
    if (required && !can(required)) setActiveTab('overview');
  }, [activeTab, can]);

  const openClinicalEvolution = useCallback(
    (evolutionId) => {
      if (!evolutionId) {
        showError(
          'Evolução clínica vinculada não localizada.'
        );
        return;
      }

      setFocusedEvolutionId(evolutionId);
      setActiveTab('evolution');

      const url = new URL(
        window.location.href
      );

      url.searchParams.set(
        'tab',
        'evolution'
      );

      window.history.replaceState(
        {},
        '',
        url.toString()
      );
    },
    [showError]
  );

  const openAlignerEvent = useCallback(
    ({
      treatmentId,
      eventId,
    } = {}) => {
    
      if (!treatmentId || !eventId) {
        showError(
          'O vínculo com o evento de alinhadores não foi localizado.'
        );
        return;
      }

      setFocusedAlignerEvent({
        treatmentId: String(
          treatmentId
        ),
        eventId: String(eventId),
      });

      setActiveTab('aligners');

      const url = new URL(
        window.location.href
      );

      url.searchParams.set(
        'tab',
        'aligners'
      );

      window.history.replaceState(
        {},
        '',
        url.toString()
      );
    },
    [showError]
  );

  const handleFocusedAlignerEventHandled =
    useCallback(() => {
      setFocusedAlignerEvent(null);
    }, []);

  const handleFocusedEvolutionHandled =
    useCallback(() => {
      setFocusedEvolutionId(null);
    }, []);

  // Dados agregados do paciente
  const patientBundleRequestRef = useRef(0);
  const [appointmentLoad, setAppointmentLoad] = useState({ patientId: '', loading: false, error: '' });

  const [patientData, setPatientData] = useState({
    images: [],
    anamnese: [],
    odontogram: null,
    treatments: [],
    appointments: [],
    invoices: [],
    payments: [],
  });

  const photoInputRef = useRef(null);
  const patientPhotoObjectUrlRef = useRef(null);
  const patientPhotoRequestIdRef = useRef(0);
  const [patientPhotoUrl, setPatientPhotoUrl] = useState(null);
  const [patientPhotoLoading, setPatientPhotoLoading] = useState(false);
  const [patientPhotoUploading, setPatientPhotoUploading] = useState(false);
  const [patientPhotoLinkLoading, setPatientPhotoLinkLoading] = useState(false);  
  const [refreshingPatientPhoto, setRefreshingPatientPhoto] = useState(false);
  const isMobileOrTablet =
    typeof window !== 'undefined' &&
    /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(window.navigator.userAgent);

  // Modais de imagem
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageForView, setSelectedImageForView] = useState(null);

  // Em atendimento (check-in via Agenda ou via extensão)
  const [activeAppt, setActiveAppt] = useState(null);

  const [showSnapshotHistory, setShowSnapshotHistory] = useState(false);
  const [snapshotHistory, setSnapshotHistory] = useState([]);
  const [loadingSnapshots, setLoadingSnapshots] = useState(false);

  const [exportingProntuario, setExportingProntuario] = useState(false);

  const [generatedReceiptId, setGeneratedReceiptId] = useState(null);

  const [receiptToOpenId, setReceiptToOpenId] = useState(null);

  const [showAuditHistory, setShowAuditHistory] = useState(false);
  const [auditHistory, setAuditHistory] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);

  const [clinicSettingsForActions, setClinicSettingsForActions] = useState(null);
  const [sendingOnlineBookingWhatsApp, setSendingOnlineBookingWhatsApp] = useState(false);

  const [sendingPhotoLinkWhatsApp, setSendingPhotoLinkWhatsApp] = useState(false);

  const token = localStorage.getItem('token');
  const headersAuth = { Authorization: `Bearer ${token}` };

  const loadClinicSettingsForActions = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/clinic-settings`, {
        headers: headersAuth,
      });

      setClinicSettingsForActions(data || null);
    } catch (error) {
      console.warn('Erro ao carregar configurações da clínica:', error);
    }
  }, []);

  useEffect(() => {
    loadClinicSettingsForActions();
  }, [loadClinicSettingsForActions]);

  const hasAlignersModule = true;

  useEffect(() => {
    if (
      clinicSettingsForActions &&
      !hasAlignersModule &&
      activeTab === 'aligners'
    ) {
      setActiveTab('overview');

      const url = new URL(window.location.href);
      url.searchParams.set('tab', 'overview');

      window.history.replaceState(
        {},
        '',
        url.toString()
      );
    }
  }, [
    clinicSettingsForActions,
    hasAlignersModule,
    activeTab,
  ]);

  // Helpers de formatação
  const formatCurrency = useCallback((value) => {
    if (value == null || Number.isNaN(Number(value))) return 'R$ 0,00';
    return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }, []);

  const formatDate = useCallback((input) => {
    if (!input) return '—';
    try {
      const d = new Date(input);
      if (isNaN(d.getTime())) return String(input);
      return d.toLocaleDateString('pt-BR');
    } catch {
      return String(input);
    }
  }, []);

  const formatDateTimeBR = useCallback((input) => {
    if (!input) return '—';

    try {
      let value = String(input);

      if (/^\d{4}-\d{2}-\d{2}T/.test(value) && !/[zZ]|[+-]\d{2}:\d{2}$/.test(value)) {
        value = `${value}Z`;
      }

      const d = new Date(value);
      if (isNaN(d.getTime())) return String(input);

      return d.toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
      });
    } catch {
      return String(input);
    }
  }, []);
  
  const getTotalTreatmentCost = useCallback((treatments) => {
    if (!Array.isArray(treatments)) return 0;
    return treatments.reduce((sum, t) => sum + (Number(t.price || 0)), 0);
  }, []);

  const getTotalInvoiceAmount = useCallback((invoices) => {
    if (!Array.isArray(invoices)) return 0;
    return invoices.reduce((sum, inv) => sum + (Number(inv.total_amount || 0)), 0);
  }, []);

  const getTotalPaidAmount = useCallback((payments) => {
    if (!Array.isArray(payments)) return 0;
    return payments.reduce((sum, p) => sum + (Number(p.amount || 0)), 0);
  }, []);

  const loadAuditHistory = useCallback(async (openModal = true) => {
    if (!selectedPatient?.id) {
      showError('Selecione um paciente para visualizar a auditoria.');
      return;
    }

    try {
      setLoadingAudit(true);

      const { data } = await axios.get(
        `${API_URL}/audit/patient/${selectedPatient.id}`,
        { headers: headersAuth }
      );

      setAuditHistory(Array.isArray(data) ? data : []);

      if (openModal) {
        setShowAuditHistory(true);
      }
    } catch (error) {
      console.error('Erro ao carregar auditoria:', error);
      showError('Erro ao carregar auditoria do prontuário.');
    } finally {
      setLoadingAudit(false);
    }
  }, [selectedPatient?.id, showError]);

  const loadSnapshotHistory = useCallback(async () => {
    if (!selectedPatient?.id) {
      showError('Selecione um paciente para visualizar o histórico.');
      return;
    }

    try {
      setLoadingSnapshots(true);

      const { data } = await axios.get(
        `${API_URL}/documents/prontuario-snapshots/${selectedPatient.id}`,
        { headers: headersAuth }
      );

      setSnapshotHistory(Array.isArray(data) ? data : []);
      setShowSnapshotHistory(true);
    } catch (error) {
      console.error('Erro ao carregar histórico do prontuário:', error);
      showError(
        error?.response?.status === 404
          ? 'Rota de histórico do prontuário não encontrada no backend. Verifique se o documents.py atualizado foi implantado e se o backend foi reiniciado.'
          : 'Erro ao carregar histórico do prontuário.'
      );
    } finally {
      setLoadingSnapshots(false);
    }
  }, [selectedPatient?.id, showError]);

  const downloadSnapshotPdf = useCallback(async (snapshotId) => {
    if (!snapshotId) return;

    try {
      const { data } = await axios.get(
        `${API_URL}/documents/prontuario-snapshots/download/${snapshotId}`,
        { headers: headersAuth }
      );

      if (!data?.pdf_base64) {
        showError('PDF antigo não encontrado neste snapshot.');
        return;
      }

      const byteCharacters = atob(data.pdf_base64);
      const byteNumbers = Array.from(byteCharacters, (char) => char.charCodeAt(0));
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = data.file_name || `prontuario_versao_${data.version || ''}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao baixar snapshot do prontuário:', error);
      showError('Erro ao baixar PDF antigo.');
    }
  }, [showError, headersAuth]);

  const createProntuarioDeliveryReceipt = async ({
    fileName,
    hash,
    exportPurpose,
    exportRequester,
    exportNotes,
  }) => {
    try {
      const shouldCreateReceipt = [
        'solicitacao_paciente',
        'solicitacao_responsavel_representante',
      ].includes(exportPurpose);

      if (!shouldCreateReceipt || !selectedPatient?.id) return null;

      const templates = await listContractTemplates();
      const receiptTemplate = Array.isArray(templates)
        ? templates.find((t) => t.system_key === 'recibo_entrega_prontuario_v1')
        : null;

      if (!receiptTemplate) {
        console.warn('Template de recibo de entrega de prontuário não encontrado.');
        return;
      }

      const dynamicValues = {
        tipo_entrega:
          exportPurpose === 'solicitacao_responsavel_representante'
            ? 'Cópia solicitada por responsável/representante'
            : 'Cópia solicitada pelo paciente',
        solicitante_recebedor: exportRequester || selectedPatient.full_name || '',
        data_hora_entrega: new Date().toLocaleString('pt-BR'),
        arquivo_entregue: fileName || '',
        hash_pdf: hash || '',
        finalidade_entrega:
          exportPurpose === 'solicitacao_responsavel_representante'
            ? 'Entrega de cópia do prontuário ao responsável/representante indicado.'
            : 'Entrega de cópia do prontuário ao paciente.',
        observacoes_entrega: exportNotes || '',
      };

      const createdReceipt = await createContract({
        template_id: receiptTemplate.id || receiptTemplate._id,
        patient_id: selectedPatient.id,
        patient_name: selectedPatient.full_name || '',
        patient_cpf: selectedPatient.cpf || '',
        patient_rg: selectedPatient.rg || '',
        patient_address: selectedPatient.address || '',

        patient_is_minor: !!selectedPatient.is_minor_or_dependent,
        is_minor_or_dependent: !!selectedPatient.is_minor_or_dependent,

        legal_guardian_name: selectedPatient.legal_guardian_name || '',
        legal_guardian_cpf: selectedPatient.legal_guardian_cpf || '',
        legal_guardian_phone: selectedPatient.legal_guardian_phone || '',
        legal_guardian_relationship: selectedPatient.legal_guardian_relationship || '',
        legal_guardian_type: selectedPatient.legal_guardian_type || '',
        legal_guardian_basis: selectedPatient.legal_guardian_basis || '',

        title: receiptTemplate.title || 'RECIBO DE ENTREGA DE CÓPIA DE PRONTUÁRIO ODONTOLÓGICO',
        contract_text: '',
        dynamic_fields: receiptTemplate.dynamic_fields || [],
        dynamic_fields_values: dynamicValues,
      });
      return createdReceipt;
    } catch (error) {
      console.warn('Não foi possível criar automaticamente o recibo de entrega do prontuário:', error);
    }
  };

  const logAudit = async (action, details = {}) => {
    try {
      await axios.post(
        `${API_URL}/audit/log`,
        {
          patient_id: selectedPatient?.id,
          action,
          details,
        },
        { headers: headersAuth }
      );
    } catch (e) {
      console.warn('Erro ao registrar auditoria:', e);
    }
  };
  
  const generateProntuarioPdf = useCallback(async ({
    exportPurpose = 'uso_interno_auditoria',
    exportRequester = '',
    exportNotes = '',
  } = {}) => {
    if (!selectedPatient) {
      showError('Selecione um paciente para gerar o PDF do prontuário.');
      return;
    }

    setExportingProntuario(true);

    await new Promise((resolve) => setTimeout(resolve, 100));

    const temp = document.createElement('div');
    temp.style.position = 'fixed';
    temp.style.left = '-9999px';
    temp.style.top = '0';
    temp.style.width = '794px';
    temp.style.background = '#ffffff';
    temp.style.color = '#111827';
    temp.style.padding = '32px';
    temp.style.fontFamily = 'Arial, sans-serif';
    temp.style.fontSize = '12px';
    temp.style.lineHeight = '1.8';

    const safe = (v) => (v === null || v === undefined || v === '' ? '—' : String(v));

    const fmt = (v) => {
      if (!v) return '—';
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
        const [y, m, d] = v.split('-');
        return `${d}/${m}/${y}`;
      }
      try {
        const d = new Date(v);
        if (isNaN(d.getTime())) return String(v);
        return d.toLocaleString('pt-BR');
      } catch {
        return String(v);
      }
    };

    const money = (v) => (formatCurrency ? formatCurrency(v) : safe(v));

    const treatmentMoneySummary = (t) => {
      const gross = money(t?.cost);
      const discountPercent =
        t?.discount_percent !== null &&
        t?.discount_percent !== undefined &&
        t?.discount_percent !== ''
          ? `${t.discount_percent}%`
          : '—';

      const discountAmount =
        t?.discount_amount !== null &&
        t?.discount_amount !== undefined &&
        t?.discount_amount !== ''
          ? money(t.discount_amount)
          : '—';

      const finalValue =
        t?.final_cost !== null &&
        t?.final_cost !== undefined &&
        t?.final_cost !== ''
          ? money(t.final_cost)
          : gross;

      return `
        <span><strong>Valor bruto:</strong> ${gross}</span><br/>
        <span><strong>Desconto:</strong> ${discountPercent} ${discountAmount !== '—' ? `(${discountAmount})` : ''}</span><br/>
        <span><strong>Valor final:</strong> ${finalValue}</span><br/>
      `;
    };

    const escapeHtml = (value) =>
      safe(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');

    const renderAnswers = (a) => {
      const answers = Array.isArray(a?.answers_resolved)
        ? a.answers_resolved
        : Array.isArray(a?.answers)
        ? a.answers
        : [];

      if (!answers.length) {
        return '<p>Respostas não disponíveis no formato detalhado.</p>';
      }

      return answers.map((item, idx) => {
        const question =
          item.label ||
          item.question_text ||
          item.question ||
          item.key ||
          item.question_id ||
          `Pergunta ${idx + 1}`;

        const answer =
          item.value ??
          item.answer ??
          item.response ??
          item.text ??
          '—';

        return `
          <div style="margin-bottom:6px;">
            <strong>${escapeHtml(question)}:</strong> ${escapeHtml(Array.isArray(answer) ? answer.join(', ') : answer)}
          </div>
        `;
      }).join('');
    };

    const translateToothStatus = (value) => {
      const map = {
        healthy: 'Saudável',
        normal: 'Normal',
        carious: 'Cárie',
        restored: 'Restaurado',
        extracted: 'Extraído',
        fractured: 'Fratura',
        crown: 'Coroa',
        prosthesis: 'Prótese',
      };

      return map[value] || value || '—';
    };

    const translateSurface = (value) => {
      const map = {
        mesial: 'Mesial',
        distal: 'Distal',
        vestibular: 'Vestibular',
        oclusal: 'Oclusal',
        lingual: 'Lingual',
        palatino: 'Palatino',
      };

      return map[value] || value || '—';
    };

    const renderAuditLogs = (logs = []) => {
      if (!Array.isArray(logs) || logs.length === 0) {
        return '<p>Nenhum registro de auditoria encontrado para este paciente.</p>';
      }

      return logs.map((log) => `
        <div style="margin-bottom:8px;padding:6px;border:1px solid #e5e7eb;border-radius:6px;">
          <strong>${escapeHtml(formatAuditAction(log.action))}</strong><br/>
          <span><strong>Data/hora:</strong> ${escapeHtml(log.created_at_br || formatDateTimeBR(log.created_at))}</span><br/>
          ${log.ip ? `<span><strong>IP:</strong> ${escapeHtml(log.ip)}</span><br/>` : ''}
          ${log.user_agent ? `<span><strong>Dispositivo/navegador:</strong> ${escapeHtml(log.user_agent)}</span><br/>` : ''}
          ${log.appointment_id ? `<span><strong>Agendamento/Evolução:</strong> ${escapeHtml(log.appointment_id)}</span><br/>` : ''}
          ${log.treatment_id ? `<span><strong>Tratamento:</strong> ${escapeHtml(log.treatment_id)}</span><br/>` : ''}
        </div>
      `).join('');
    };

    const translateDocumentStatus = (status) => {
      const map = {
        draft: 'Rascunho',
        signed: 'Assinado',
        revoked: 'Revogado',
        finalized: 'Finalizado',
        pending: 'Pendente',
      };

      return map[status] || status || '—';
    };

    const renderContracts = (docs = []) => {
      if (!Array.isArray(docs) || docs.length === 0) {
        return '<p>Nenhum contrato ou termo assinado registrado.</p>';
      }

      return docs.map((doc) => `
        <div style="margin-bottom:8px;padding:6px;border:1px solid #e5e7eb;border-radius:6px;">
          <strong>${escapeHtml(doc.title || doc.template_name || 'Documento')}</strong><br/>
          <span><strong>Status:</strong> ${escapeHtml(translateDocumentStatus(doc.status))}</span><br/>
          ${doc.signed_at ? `<span><strong>Assinado em:</strong> ${escapeHtml(fmt(doc.signed_at))}</span><br/>` : ''}
          ${doc.patient_name ? `<span><strong>Paciente:</strong> ${escapeHtml(doc.patient_name)}</span><br/>` : ''}
          ${doc.validation_code ? `<span><strong>Código de validação:</strong> ${escapeHtml(doc.validation_code)}</span><br/>` : ''}
          ${doc.hash_sha256 ? `<span style="font-size:10px;color:#6b7280;">Hash: ${doc.hash_sha256}</span>` : ''}
        </div>
      `).join('');
    };

    const renderOdontogram = () => {
      const teeth = patientData?.odontogram?.teeth || [];

      if (!Array.isArray(teeth) || teeth.length === 0) {
        return '<p>Nenhum registro no odontograma.</p>';
      }

      const relevant = teeth.filter((t) => {
        if (!t) return false;
        if (t.status && t.status !== 'healthy') return true;

        const s = t.surfaces || {};
        if (Object.keys(s).length > 0) return true;

        if ((t.procedures || []).length > 0) return true;
        if ((t.planned_procedures || []).length > 0) return true;

        return false;
      });

      if (!relevant.length) {
        return '<p>Odontograma sem alterações relevantes.</p>';
      }

      return relevant.map((t) => {
        const surfaces = t.surfaces || {};
        const surfaceList = Object.entries(surfaces)
          .map(([k, v]) => `${translateSurface(k)}: ${translateToothStatus(v)}`)
          .join(', ');

        return `
          <div style="margin-bottom:8px;padding:6px;border:1px solid #e5e7eb;border-radius:6px;">
            <strong>Dente ${safe(t.tooth_number)}</strong>
            ${t.status && t.status !== 'healthy' ? `— ${translateToothStatus(t.status)}` : ''}<br/>

            ${surfaceList ? `<span><strong>Superfícies:</strong> ${surfaceList}</span><br/>` : ''}

            ${(t.procedures || []).length > 0
              ? `<span><strong>Procedimentos realizados:</strong> ${(t.procedures || []).join(', ')}</span><br/>`
              : ''}

            ${(t.planned_procedures || []).length > 0
              ? `<span><strong>Planejados:</strong> ${(t.planned_procedures || []).join(', ')}</span><br/>`
              : ''}

            ${t.notes ? `<span><strong>Observações:</strong> ${escapeHtml(t.notes)}</span><br/>` : ''}

            ${t.last_updated ? `<span style="font-size:10px;color:#6b7280;">Atualizado em: ${fmt(t.last_updated)}</span>` : ''}
          </div>
        `;
      }).join('');
    };

    const makeHash = async (text) => {
      const data = new TextEncoder().encode(text);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      return Array.from(new Uint8Array(hashBuffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
    };

    const treatments = Array.isArray(patientData?.treatments) ? patientData.treatments : [];
    const appointments = Array.isArray(patientData?.appointments) ? patientData.appointments : [];
    const anamnese = Array.isArray(patientData?.anamnese) ? patientData.anamnese : [];
    const images = Array.isArray(patientData?.images) ? patientData.images : [];
    const invoices = Array.isArray(patientData?.invoices) ? patientData.invoices : [];

    const payments = Array.isArray(patientData?.payments) ? patientData.payments : [];

    const isInternalExport = exportPurpose === 'uso_interno_auditoria';
    const isPatientCopy = [
      'solicitacao_paciente',
      'solicitacao_responsavel_representante',
    ].includes(exportPurpose);

    const exportPurposeLabel = {
      uso_interno_auditoria: 'Uso interno / auditoria',
      solicitacao_paciente: 'Cópia solicitada pelo paciente',
      solicitacao_responsavel_representante: 'Cópia solicitada por responsável/representante',
      encaminhamento_outro_profissional: 'Encaminhamento para outro profissional',
      solicitacao_judicial: 'Solicitação judicial',
      outro: 'Outro',
    }[exportPurpose] || 'Uso interno / auditoria';

    const totalTreatments = getTotalTreatmentCost(treatments);
    const totalInvoiced = getTotalInvoiceAmount(invoices);
    const totalPaid = getTotalPaidAmount(payments);
    const balance = totalInvoiced - totalPaid;

    const evolutionItems = appointments
      .filter((a) => ['Realizado', 'Não Compareceu', 'Registro Inativado'].includes(a.status))
      .sort((a, b) => `${a.appointment_date || ''} ${a.start_time || ''}`.localeCompare(`${b.appointment_date || ''} ${b.start_time || ''}`));

    let auditLogs = [];
    let contractDocuments = [];
    try {
      const contractsRes = await axios.get(
        `${API_URL}/contracts/documents?patient_id=${selectedPatient.id}`,
        { headers: headersAuth }
      );
      contractDocuments = Array.isArray(contractsRes.data) ? contractsRes.data : [];
    } catch (e) {
      console.warn('Erro ao carregar contratos para o PDF:', e);
    }
    try {
      const auditRes = await axios.get(
        `${API_URL}/audit/patient/${selectedPatient.id}`,
        { headers: headersAuth }
      );
      auditLogs = Array.isArray(auditRes.data) ? auditRes.data : [];
    } catch (error) {
      console.warn('Não foi possível carregar auditoria para o PDF:', error);
    }

    let snapshotHistoryPdf = [];
    try {
      const snapRes = await axios.get(
        `${API_URL}/documents/prontuario-snapshots/${selectedPatient.id}`,
        { headers: headersAuth }
      );
      snapshotHistoryPdf = Array.isArray(snapRes.data) ? snapRes.data : [];
    } catch (e) {
      console.warn('Erro ao carregar histórico de versões:', e);
    }

    let clinicSettings = {};
    try {
      const clinicRes = await axios.get(`${API_URL}/clinic-settings`, {
        headers: headersAuth,
      });
      clinicSettings = clinicRes.data || {};
    } catch (error) {
      console.warn('Não foi possível carregar configurações da clínica para o PDF:', error);
    }

    const clinicName =
      clinicSettings.clinic_name ||
      'Clínica odontológica';

    const clinicAddress =
      clinicSettings.address ||
      [
        clinicSettings.address_street,
        clinicSettings.address_number,
        clinicSettings.address_complement,
        clinicSettings.address_neighborhood,
        clinicSettings.address_city,
        clinicSettings.address_state,
        clinicSettings.address_cep ? `CEP ${clinicSettings.address_cep}` : '',
      ]
        .filter(Boolean)
        .join(' / ') ||
      'Endereço não informado';

    const clinicPhone = clinicSettings.phone || '';
    const clinicEmail = clinicSettings.email || '';

    const html = `
      <div>
        <div style="border-bottom:1px solid #e5e7eb;padding-bottom:10px;margin-bottom:14px;">
          <div style="font-size:18px;font-weight:bold;">${escapeHtml(clinicName)}</div>
          <div style="font-size:11px;color:#4b5563;">
            ${clinicPhone ? `Telefone: ${escapeHtml(clinicPhone)}` : ''}
            ${clinicPhone && clinicEmail ? ' • ' : ''}
            ${clinicEmail ? `E-mail: ${escapeHtml(clinicEmail)}` : ''}
          </div>
        </div>

        <h1 style="font-size:22px;margin:0 0 4px;">
          ${isPatientCopy ? 'Cópia do Prontuário Odontológico' : 'Relatório Técnico do Prontuário Odontológico'}
        </h1>
        <div style="font-size:11px;color:#6b7280;margin-bottom:12px;">
          Gerado em ${new Date().toLocaleString('pt-BR')} pelo OdontoAssist AI
        </div>

        <div style="padding:10px;border:1px solid #e5e7eb;border-radius:6px;background:#f9fafb;margin-bottom:18px;font-size:11px;">
          <div><strong>Tipo de emissão:</strong> ${escapeHtml(exportPurposeLabel)}</div>
          ${exportRequester ? `<div><strong>Solicitante/recebedor:</strong> ${escapeHtml(exportRequester)}</div>` : ''}
          ${exportNotes ? `<div><strong>Observações da emissão:</strong> ${escapeHtml(exportNotes)}</div>` : ''}
          <div style="margin-top:6px;">
            <strong>Escopo:</strong>
            ${isPatientCopy
              ? 'cópia dos registros clínicos disponíveis no prontuário odontológico na data de geração, para acesso do paciente/representante.'
              : 'documento consolidado para controle interno, auditoria, continuidade assistencial e eventual comprovação técnica/jurídica dos registros existentes no sistema na data de geração.'}
          </div>
          <div style="margin-top:6px;color:#6b7280;">
            Documentos autônomos assinados, contratos, consentimentos, recibos e arquivos originais devem ser consultados em suas versões próprias quando necessário.
          </div>
        </div>

        <h2 style="font-size:16px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;">1. Identificação</h2>
        <p><strong>Nome:</strong> ${safe(selectedPatient.full_name)}</p>
        <p><strong>Nome social / preferido:</strong> ${safe(selectedPatient.preferred_name)}</p>
        <p><strong>CPF:</strong> ${safe(formatCpfDisplay(selectedPatient.cpf))} &nbsp; <strong>RG:</strong> ${safe(selectedPatient.rg)}</p>
        <p><strong>Nascimento:</strong> ${fmt(selectedPatient.birth_date)} &nbsp; <strong>Telefone:</strong> ${safe(formatPhoneDisplay(selectedPatient.phone))}</p>
        <p><strong>E-mail:</strong> ${safe(selectedPatient.email)}</p>
        <p><strong>Endereço:</strong> ${safe(selectedPatient.address)}</p>
        <p><strong>Profissão:</strong> ${safe(selectedPatient.profession)} &nbsp; <strong>Estado civil:</strong> ${safe(selectedPatient.marital_status)}</p>
        ${selectedPatient.is_minor_or_dependent || selectedPatient.legal_guardian_name ? `
          <p><strong>Responsável/representante:</strong> ${safe(selectedPatient.legal_guardian_name)}</p>
          <p><strong>CPF do responsável/representante:</strong> ${safe(selectedPatient.legal_guardian_cpf)} &nbsp; <strong>Telefone:</strong> ${safe(selectedPatient.legal_guardian_phone)}</p>
          <p><strong>Vínculo/base da representação:</strong> ${safe(selectedPatient.legal_guardian_relationship)} ${selectedPatient.legal_guardian_basis ? `— ${safe(selectedPatient.legal_guardian_basis)}` : ''}</p>
        ` : ''}
        <p><strong>Contato de emergência:</strong> ${safe(selectedPatient.emergency_contact_name)} ${selectedPatient.emergency_contact_phone ? `— ${safe(selectedPatient.emergency_contact_phone)}` : ''}</p>
        <p><strong>Pessoa autorizada a receber informações:</strong> ${safe(selectedPatient.authorized_contact_name)} ${selectedPatient.authorized_contact_phone ? `— ${safe(selectedPatient.authorized_contact_phone)}` : ''} ${selectedPatient.authorized_contact_relationship ? `— ${safe(selectedPatient.authorized_contact_relationship)}` : ''}</p>
        <p><strong>Necessidades de acessibilidade:</strong> ${safe(selectedPatient.accessibility_needs)}</p>
        <p><strong>Diretivas antecipadas:</strong> ${safe(selectedPatient.advance_directives)}</p>
        <p><strong>Alertas clínicos:</strong> ${safe(selectedPatient.clinical_alerts)}</p>

        <h2 style="font-size:16px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-top:18px;">2. Anamnese</h2>
        ${
          anamnese.length
            ? anamnese.map((a, idx) => `
              <div style="margin-bottom:10px;padding:8px;border:1px solid #e5e7eb;border-radius:6px;">
                <strong>Anamnese ${idx + 1}</strong><br/>
                <span><strong>Data:</strong> ${fmt(a.created_at || a.submitted_at)}</span><br/>
                <span><strong>Template:</strong> ${escapeHtml(a.template_title || a.template_name || a.template_id)}</span>
                <div style="margin-top:8px;">
                  ${renderAnswers(a)}
                </div>
              </div>
            `).join('')
            : '<p>Nenhuma anamnese registrada.</p>'
        }

        <h2 style="font-size:16px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-top:18px;">
        3. Odontograma (Resumo Clínico)
        </h2>

        ${renderOdontogram()}

        <h2 style="font-size:16px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-top:18px;">4. Planos de tratamento</h2>
        ${
          treatments.length
            ? treatments.map((t) => `
              <div style="margin-bottom:10px;padding:8px;border:1px solid #e5e7eb;border-radius:6px;">
                <strong>${safe(t.procedure)}</strong> — ${safe(t.status)}<br/>
                <span><strong>Diagnóstico:</strong> ${safe(t.diagnosis)}</span><br/>
                <span><strong>Plano:</strong> ${safe(t.treatment_plan)}</span><br/>
                <span><strong>Alternativas:</strong> ${safe(t.alternatives)}</span><br/>
                <span><strong>Riscos:</strong> ${safe(t.risks)}</span><br/>
                ${isInternalExport ? treatmentMoneySummary(t) : ''}
                <span><strong>Aceite:</strong> ${
                  t.patient_acceptance_status === 'aceito'
                    ? `Aceito por link em ${fmt(t.patient_acceptance_at)}${t.patient_acceptance_ip ? ` — IP ${safe(t.patient_acceptance_ip)}` : ''}`
                    : safe(t.patient_acceptance_status || 'pendente')
                }</span>
                ${
                  t.is_inactive
                    ? `<br/><span style="color:#b91c1c;"><strong>Inativado:</strong> ${fmt(t.inactive_at)} — Motivo: ${safe(t.inactive_reason)}</span>`
                    : ''
                }
              </div>
            `).join('')
            : '<p>Nenhum plano de tratamento registrado.</p>'
        }

        <h2 style="font-size:16px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-top:18px;">5. Evolução clínica</h2>
        ${
          evolutionItems.length
            ? evolutionItems.map((a) => `
              <div style="margin-bottom:10px;padding:8px;border:1px solid #e5e7eb;border-radius:6px;">
                <strong>${fmt(a.appointment_date)} ${safe(a.start_time)}</strong> — ${safe(a.procedure)} — ${safe(a.status)}<br/>
                <span>${safe(a.evolution_notes)}</span><br/>
                <span><strong>Profissional:</strong> ${safe(a.professional_name)} ${a.professional_cro ? `— ${safe(a.professional_cro)}` : ''}</span><br/>
                <span><strong>Ciência:</strong> ${
                  a.patient_acknowledgement_status === 'ciente'
                    ? `Confirmada por link em ${fmt(a.patient_acknowledgement_at)}${a.patient_acknowledgement_ip ? ` — IP ${safe(a.patient_acknowledgement_ip)}` : ''}`
                    : safe(a.patient_acknowledgement_status || 'pendente')
                }</span>
                ${
                  a.is_inactive
                    ? `<br/><span style="color:#b91c1c;"><strong>Registro inativado:</strong> ${fmt(a.inactive_at)} — Motivo: ${safe(a.inactive_reason)}</span>`
                    : ''
                }
              </div>
            `).join('')
            : '<p>Nenhuma evolução clínica registrada.</p>'
        }

        <h2 style="font-size:16px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-top:18px;">6. Imagens, exames e anexos</h2>
        ${
          images.length
            ? images.map((img) => `
              <div style="margin-bottom:6px;">
                <strong>${safe(img.filename || img.name || img.title || 'Arquivo')}</strong>
                — ${safe(img.image_type || img.type || 'anexo')}
                — ${fmt(img.created_at || img.uploaded_at)}
              </div>
            `).join('')
            : '<p>Nenhuma imagem ou anexo registrado.</p>'
        }

        ${isInternalExport ? `
          <h2 style="font-size:16px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-top:18px;">
          Financeiro vinculado
          </h2>

          <div style="margin-top:8px;padding:10px;border:1px solid #e5e7eb;border-radius:6px;background:#fafafa;">
            <div><strong>Total dos planos de tratamento:</strong> ${money(totalTreatments)}</div>
            <div><strong>Total faturado:</strong> ${money(totalInvoiced)}</div>
            <div><strong>Total pago:</strong> ${money(totalPaid)}</div>
            <div><strong>Saldo em aberto:</strong> ${money(balance)}</div>
          </div>

          <div style="margin-top:8px;font-size:10px;color:#6b7280;">
            As informações financeiras são apresentadas exclusivamente para fins administrativos e não constituem comprovante fiscal ou recibo.
          </div>
        ` : ''}

        <h2 style="font-size:16px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-top:18px;">
        Documentos, contratos e consentimentos assinados
        </h2>

        ${renderContracts(contractDocuments)}

        ${isInternalExport ? `
          <h2 style="font-size:16px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-top:18px;">
          Auditoria e rastreabilidade
          </h2>

          ${renderAuditLogs(auditLogs)}
        ` : ''}

        ${isInternalExport ? `
          <h2 style="font-size:16px;border-bottom:1px solid #e5e7eb;padding-bottom:4px;margin-top:18px;">
          Histórico de versões do prontuário
          </h2>

          ${
            snapshotHistoryPdf.length
              ? snapshotHistoryPdf.map((s) => `
                <div style="margin-bottom:6px;">
                  Versão ${s.version || '—'} —
                  ${fmt(s.created_at)} —
                  Hash: ${s.hash_sha256 || '—'}
                </div>
              `).join('')
              : '<p>Nenhuma versão anterior registrada.</p>'
          }
        ` : ''}

        <div style="margin-top:28px;border-top:1px solid #e5e7eb;padding-top:10px;font-size:10px;color:#6b7280;">
          Este documento é um resumo consolidado dos registros disponíveis no sistema na data de geração.
        </div>
      </div>
    `;

    const mainProfessional =
      Array.isArray(clinicSettings.professionals)
        ? clinicSettings.professionals.find((p) => p.is_main) || clinicSettings.professionals[0]
        : null;

    const professionalName =
      mainProfessional?.name ||
      clinicSettings.professional_name ||
      user?.full_name ||
      user?.name ||
      'Profissional responsável';

    const croNumber =
      mainProfessional?.cro_number ||
      clinicSettings.cro_number ||
      '';

    const croState =
      mainProfessional?.cro_state ||
      clinicSettings.cro_state ||
      '';

    const professionalCro =
      croNumber
        ? `CRO${croState ? `-${croState}` : ''} ${croNumber}`
        : 'CRO não informado';

    const signatureBlock = `
      <div style="margin-top:36px;padding-top:18px;border-top:1px solid #e5e7eb;">
        <div style="font-size:12px;font-weight:bold;margin-bottom:24px;">
          Assinatura profissional / validação do relatório
        </div>

        <div style="margin-top:32px;width:320px;text-align:center;">
          <div style="border-top:1px solid #111827;margin-bottom:6px;"></div>
          <div><strong>${escapeHtml(professionalName)}</strong></div>
          <div>${escapeHtml(professionalCro)}</div>
          <div style="font-size:10px;color:#6b7280;margin-top:4px;">
            Relatório gerado eletronicamente no sistema OdontoAssist AI
          </div>
        </div>
      </div>
    `;

    const htmlWithSignature = html.replace(
      '<div style="margin-top:28px;border-top:1px solid #e5e7eb;padding-top:10px;font-size:10px;color:#6b7280;">',
      `${signatureBlock}<div style="margin-top:28px;border-top:1px solid #e5e7eb;padding-top:10px;font-size:10px;color:#6b7280;">`
    );

    const integrityHash = await makeHash(htmlWithSignature);

    const integrityBlock = `
      <div style="margin-top:28px;border-top:1px solid #e5e7eb;padding-top:10px;font-size:10px;color:#374151;">
        <strong>Registro de integridade:</strong><br/>
        Hash SHA-256 do conteúdo-base: ${integrityHash}<br/>
        Paciente ID: ${safe(selectedPatient.id)}<br/>
        Data/hora de geração: ${new Date().toLocaleString('pt-BR')}<br/>
        Sistema: OdontoAssist AI
      </div>
    `;

    temp.innerHTML = htmlWithSignature.replace(
      '<div style="margin-top:28px;border-top:1px solid #e5e7eb;padding-top:10px;font-size:10px;color:#6b7280;">',
      `${integrityBlock}<div style="margin-top:28px;border-top:1px solid #e5e7eb;padding-top:10px;font-size:10px;color:#6b7280;">`
    );

    document.body.appendChild(temp);

    try {
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const marginX = 40;
      const headerHeight = 58;
      const footerHeight = 46;
      const contentTop = headerHeight + 18;
      const contentBottom = pageHeight - footerHeight - 16;
      const contentWidth = pageWidth - marginX * 2;

      const children = Array.from(temp.firstElementChild.children);

      let pageNumber = 1;
      let cursorY = contentTop;

      const drawHeaderFooter = () => {
        pdf.setFontSize(9);
        pdf.setTextColor(80);

        pdf.text(String(clinicName || 'Clínica odontológica'), marginX, 28);
        pdf.setFontSize(8);
        pdf.text(String(clinicAddress || 'Endereço não informado'), marginX, 41);
        pdf.line(marginX, 50, pageWidth - marginX, 50);

        pdf.setFontSize(9);
        pdf.setTextColor(100);
        pdf.line(marginX, pageHeight - 42, pageWidth - marginX, pageHeight - 42);
        pdf.text(`OdontoAssist AI • Página ${pageNumber}`, marginX, pageHeight - 25);
        pdf.text(new Date().toLocaleString('pt-BR'), pageWidth - 160, pageHeight - 25);
      };

      drawHeaderFooter();

      for (const child of children) {
        const wrapper = document.createElement('div');
        wrapper.style.width = '714px';
        wrapper.style.background = '#ffffff';
        wrapper.style.color = '#111827';
        wrapper.style.fontFamily = 'Arial, sans-serif';
        wrapper.style.position = 'fixed';
        wrapper.style.left = '-9999px';
        wrapper.style.top = '0';
        wrapper.style.fontSize = '12px';
        wrapper.style.lineHeight = '1.8';
        wrapper.style.padding = '8px 0 12px 0';
        wrapper.style.overflow = 'visible';
        wrapper.appendChild(child.cloneNode(true));

        document.body.appendChild(wrapper);

        const canvas = await html2canvas(wrapper, {
          scale: 1.6,
          useCORS: true,
          backgroundColor: '#ffffff',
        });

        document.body.removeChild(wrapper);

        const imgData = canvas.toDataURL('image/png');
        const imgHeight = (canvas.height * contentWidth) / canvas.width;

        if (cursorY + imgHeight > contentBottom && cursorY > contentTop) {
          pdf.addPage();
          pageNumber += 1;
          cursorY = contentTop;
          drawHeaderFooter();
        }

        if (imgHeight <= contentBottom - contentTop) {
          pdf.addImage(imgData, 'PNG', marginX, cursorY, contentWidth, imgHeight);
          cursorY += imgHeight + 10;
        } else {
          let remainingHeight = imgHeight;
          let sourceY = 0;
          const pageContentHeight = contentBottom - contentTop;

          while (remainingHeight > 0) {
            const sliceHeight = Math.min(pageContentHeight, remainingHeight);

            if (cursorY !== contentTop) {
              pdf.addPage();
              pageNumber += 1;
              cursorY = contentTop;
              drawHeaderFooter();
            }

            pdf.addImage(
              imgData,
              'JPEG',
              marginX,
              cursorY,
              contentWidth,
              imgHeight,
              undefined,
              'FAST',
              0,
              sourceY
            );

            remainingHeight -= sliceHeight;
            sourceY -= sliceHeight;

            if (remainingHeight > 0) {
              pdf.addPage();
              pageNumber += 1;
              cursorY = contentTop;
              drawHeaderFooter();
            }
          }

          cursorY = contentTop;
        }
      }

      const totalPages = pdf.getNumberOfPages();

      for (let i = 1; i <= totalPages; i += 1) {
        pdf.setPage(i);
        pdf.setFontSize(9);
        pdf.setTextColor(100);

        pdf.setFillColor(255, 255, 255);
        pdf.rect(pageWidth - 110, pageHeight - 36, 70, 14, 'F');
        pdf.text(`Página ${i} de ${totalPages}`, pageWidth - 110, pageHeight - 25);
      }

      const fileName = `prontuario_${(selectedPatient.full_name || 'paciente')
        .replace(/[^\w\s-]/g, '')
        .replace(/\s+/g, '_')}.pdf`;

      const pdfDataUri = pdf.output('datauristring');
      const pdfBase64 = pdfDataUri.split(',')[1];

      const pdfBlobForDownload = pdf.output('blob');
      const downloadUrl = URL.createObjectURL(pdfBlobForDownload);

      const downloadLink = document.createElement('a');
      downloadLink.href = downloadUrl;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);

      setTimeout(() => {
        URL.revokeObjectURL(downloadUrl);
      }, 1000);

      await axios.post(
        `${API_URL}/audit/exports/clinical-record`,
        {
          patient_id: selectedPatient.id,
          export_type: 'complete_record',
          file_name: fileName,
          hash_sha256: integrityHash,
          purpose: exportPurpose,
          requester: exportRequester,
          notes: exportNotes,
        },
        { headers: headersAuth }
      );

      try {
        await axios.post(
          `${API_URL}/documents/prontuario-snapshots`,
          {
            patient_id: selectedPatient.id,
            file_name: fileName,
            hash_sha256: integrityHash,
            pdf_base64: pdfBase64,
          },
          { headers: headersAuth }
        );
      } catch (err) {
        console.error('Erro ao salvar snapshot do prontuário:', err);
        showError(
          err?.response?.data?.detail ||
          err?.response?.statusText ||
          'PDF gerado, mas não foi possível registrar o snapshot no histórico.'
        );
        return;
      }

      const createdReceipt = await createProntuarioDeliveryReceipt({
        fileName,
        hash: integrityHash,
        exportPurpose,
        exportRequester,
        exportNotes,
      });

      if (createdReceipt?.id || createdReceipt?._id) {
        setGeneratedReceiptId(
          createdReceipt.id || createdReceipt._id
        );
      }

      showSuccess(
        ['solicitacao_paciente', 'solicitacao_responsavel_representante'].includes(exportPurpose)
          ? 'PDF do prontuário gerado e recibo de entrega criado com sucesso.'
          : 'PDF do prontuário gerado e registrado com sucesso.'
      );
    } catch (error) {
      console.error('Erro ao gerar PDF do prontuário:', error);
      showError('Erro ao gerar PDF do prontuário.');
    } 
    finally {
      setExportingProntuario(false);

      if (temp && document.body.contains(temp)) {
        document.body.removeChild(temp);
      }
    }
  }, [selectedPatient, patientData, formatDate, formatCurrency, showError, showSuccess, user]);

  // Escuta eventos para mudar de aba vindo de outros lugares (Agenda → Evolução, por ex.)
  useEffect(() => {
    const handler = (ev) => {
      if (!ev || !ev.detail) return;
      const { tab } = ev.detail;
      if (tab && typeof tab === 'string' && isValidTab(tab)) {
        setActiveTab(tab);
      }
    };
    window.addEventListener('prontuario:changeTab', handler);
    return () => window.removeEventListener('prontuario:changeTab', handler);
  }, []);

  // ====== Carregar lista de pacientes ======
  const fetchPatients = useCallback(async () => {
    try {
      const tokenLocal = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${tokenLocal}` };
      const { data } = await axios.get(`${API_URL}/patients?status=all`, { headers });
      const listRaw = Array.isArray(data) ? data : (data?.items || []);
      const list = [...listRaw].sort((a, b) =>
        (a.full_name || '').localeCompare(b.full_name || '', 'pt-BR', { sensitivity: 'base' })
      );
      setPatients(list);
      return list;
    } catch (e) {
      console.error('Erro ao carregar pacientes:', e);
      setPatients([]);
      showErrorRef.current('Erro ao carregar pacientes');
      return [];
    }
  }, []);

  // Primeiro carregamento + deep-link de patientId
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      const list = await fetchPatients();
      if (!mounted) return;

      const forceListModeNow = getQS('modo') === 'lista';

      const pidQS = (getQS('patientId') || getQS('patient_id')) ?? null;
      const pidLS = forceListModeNow ? null : localStorage.getItem('currentPatientId');
      const pid = forceListModeNow ? null : (pidQS || pidLS);

      if (forceListModeNow) {
        localStorage.removeItem('currentPatientId');
      }

      if (pid) {
        const found = list.find((p) => p.id === pid);
      if (found) {
        setSelectedPatient(found);
        setMode('detail');

        try {
          await refreshSelectedPatient(found.id);
        } catch (error) {
          console.error('Erro ao carregar paciente completo no primeiro carregamento:', error);
        }
      } else {
          setSelectedPatient(null);
          setMode('list');
          localStorage.removeItem('currentPatientId');
        }
      } else {
        setMode('list');
      }

      const tabQS = getQS('tab');
      if (isValidTab(tabQS)) {
        setActiveTab(tabQS);
      }

      setLoading(false);
    })();
    return () => { mounted = false; };
  }, [fetchPatients]);

  const loadDashboardPatientFilter = useCallback(async (filterName) => {
    if (!['inactive', 'stalled'].includes(filterName)) {
      setDashboardFilterPatientIds([]);
      setDashboardFilterDetails({});
      return;
    }

    try {
      setLoadingDashboardFilter(true);
      const tokenLocal = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${tokenLocal}` };
      const now = new Date();
      const matchedIds = new Set();
      const details = {};

      if (filterName === 'inactive') {
        const { data } = await axios.get(`${API_URL}/appointments`, { headers });
        const appointments = Array.isArray(data) ? data : (data?.items || []);
        const latestByPatient = new Map();

        appointments.forEach((appointment) => {
          const patientId = String(
            appointment?.patient_id ||
              appointment?.patient?.id ||
              appointment?.patient?._id ||
              ''
          ).trim();

          if (!patientId) return;

          const date = safeDashboardDate(
            appointment?.appointment_date ||
              appointment?.date ||
              appointment?.start_date ||
              appointment?.start
          );

          if (!date || date > now) return;

          const previous = latestByPatient.get(patientId);
          if (!previous || date > previous) {
            latestByPatient.set(patientId, date);
          }
        });

        patients.forEach((patient) => {
          if (patient.is_inactive) return;
          const patientId = String(patient?.id || patient?._id || '').trim();
          const latest = latestByPatient.get(patientId);
          if (!patientId || !latest) return;

          const days = Math.floor((now - latest) / 86400000);
          if (days < 180) return;

          matchedIds.add(patientId);
          details[patientId] = {
            days,
            label: `${days} dias desde o último atendimento`,
          };
        });
      }

      if (filterName === 'stalled') {
        const { data } = await axios.get(`${API_URL}/treatments`, { headers });
        const treatmentList = Array.isArray(data?.items)
          ? data.items
          : Array.isArray(data)
          ? data
          : [];

        treatmentList.forEach((treatment) => {
          const status = normalizeDashboardStatus(treatment?.status);
          const activeStatus =
            status.includes('andamento') ||
            status.includes('ativo') ||
            status.includes('in_progress');

          if (!activeStatus) return;

          const reference = safeDashboardDate(
            treatment?.updated_at ||
              treatment?.start_date ||
              treatment?.created_at
          );

          if (!reference) return;

          const days = Math.floor((now - reference) / 86400000);
          if (days < 90) return;

          let patientId = String(
            treatment?.patient_id ||
              treatment?.patient?.id ||
              treatment?.patient?._id ||
              ''
          ).trim();

          if (!patientId && treatment?.patient_name) {
            const normalizedName = String(treatment.patient_name).trim().toLowerCase();
            const patientMatch = patients.find(
              (patient) =>
                String(patient?.full_name || patient?.name || '')
                  .trim()
                  .toLowerCase() === normalizedName
            );
            patientId = String(patientMatch?.id || patientMatch?._id || '').trim();
          }

          if (!patientId || !patients.some(p => p.id === patientId && !p.is_inactive)) return;

          matchedIds.add(patientId);

          const previousDays = Number(details[patientId]?.days || 0);
          if (days >= previousDays) {
            details[patientId] = {
              days,
              label: `${days} dias sem atualização do tratamento`,
            };
          }
        });
      }

      setDashboardFilterPatientIds(Array.from(matchedIds));
      setDashboardFilterDetails(details);
    } catch (error) {
      console.error('Erro ao aplicar filtro vindo do Dashboard:', error);
      setDashboardFilterPatientIds([]);
      setDashboardFilterDetails({});
      showError('Não foi possível aplicar o filtro solicitado pelo Dashboard.');
    } finally {
      setLoadingDashboardFilter(false);
    }
  }, [patients, showError]);

  useEffect(() => {
    if (!patients.length || !dashboardFilter) return;
    loadDashboardPatientFilter(dashboardFilter);
  }, [patients, dashboardFilter, loadDashboardPatientFilter]);

  const clearDashboardFilter = useCallback(() => {
    setDashboardFilter('');
    setDashboardFilterPatientIds([]);
    setDashboardFilterDetails({});

    const url = new URL(window.location.href);
    url.searchParams.delete('filter');
    url.searchParams.delete('tab');
    window.history.replaceState({}, '', url.toString());
  }, []);

  // Filtro de busca e filtro contextual do Dashboard
  const filteredPatients = useMemo(
    () =>
      patients.filter((p) => {
        if (!matchesPatientStatus(p, patientStatus)) return false;
        const patientId = String(p?.id || p?._id || '').trim();

        if (
          dashboardFilter &&
          !dashboardFilterPatientIds.includes(patientId)
        ) {
          return false;
        }

        const q = (patientSearch || '').trim().toLowerCase();
        if (!q) return true;
        const name = (p.full_name || '').toLowerCase();
        const email = (p.email || '').toLowerCase();
        const phone = (p.phone || '').toLowerCase();
        return name.includes(q) || email.includes(q) || phone.includes(q);
      }),
    [
      patients,
      patientSearch,
      patientStatus,
      dashboardFilter,
      dashboardFilterPatientIds,
    ]
  );

  // ====== Carregar bundles do paciente selecionado ======
  const loadPatientBundles = useCallback(
    async (patientId) => {
      if (!patientId) return;
      const requestId = ++patientBundleRequestRef.current;
      setAppointmentLoad({ patientId, loading: true, error: '' });

      try {
        const tokenLocal = localStorage.getItem('token');
        const headers = { Authorization: `Bearer ${tokenLocal}` };
        const accept404 = { validateStatus: (s) => s >= 200 && s < 500 };

        const [
          imagesRes,
          anamneseRes,
          odontogramRes,
          treatmentsRes,
          appointmentsRes,
          invoicesRes,
          paymentsRes,
        ] = await Promise.all([
          can('images.view') ? axios.get(`${API_URL}/images?patient_id=${patientId}`, { headers }).catch(() => ({ data: [], status: 200 })) : Promise.resolve({ data: [], status: 200 }),
          axios
            .get(`${API_URL}/anamnese/responses?patient_id=${patientId}&resolve_labels=true`, { headers })
            .catch(() => ({ data: [], status: 200 })),
          can('clinical_records.view') ? axios.get(`${API_URL}/odontograma/patient/${patientId}`, { headers, ...accept404 }).catch(() => ({
            data: null,
            status: 200,
          })) : Promise.resolve({ data: null, status: 200 }),
          can('clinical_records.view') ? axios
            .get(`${API_URL}/treatments?patient_id=${patientId}`, { headers })
            .catch(() => ({ data: [], status: 200 })) : Promise.resolve({ data: [], status: 200 }),
          axios
            .get(`${API_URL}/appointments?patient_id=${encodeURIComponent(patientId)}`, { headers })
            .catch((error) => ({ data: [], status: error?.response?.status || 0, loadError: true })),
          can('billing.view') ? axios
            .get(`${API_URL}/billing/invoices?patient_id=${patientId}`, { headers })
            .catch(() => ({ data: [], status: 200 })) : Promise.resolve({ data: [], status: 200 }),
          can('billing.view') ? axios
            .get(`${API_URL}/billing/payments?patient_id=${patientId}`, { headers })
            .catch(() => ({ data: [], status: 200 })) : Promise.resolve({ data: [], status: 200 }),
        ]);

        if (requestId !== patientBundleRequestRef.current) return;
        const appointmentError = appointmentsRes.loadError
          ? (appointmentsRes.status === 403
            ? 'Seu perfil não tem permissão para consultar os agendamentos deste paciente.'
            : appointmentsRes.status === 401
            ? 'A sessão expirou. Entre novamente para consultar os agendamentos.'
            : 'Não foi possível carregar os agendamentos. Tente novamente; isso não significa que os registros foram apagados.')
          : '';
        setAppointmentLoad({ patientId, loading: false, error: appointmentError });
        setPatientData({
          images: Array.isArray(imagesRes.data) ? imagesRes.data : imagesRes.data?.items || [],
          anamnese: Array.isArray(anamneseRes.data) ? anamneseRes.data : anamneseRes.data?.items || [],
          odontogram: odontogramRes?.status === 200 ? odontogramRes.data || null : null,
          analyses: [],
          treatments: Array.isArray(treatmentsRes.data) ? treatmentsRes.data : treatmentsRes.data?.items || [],
          appointments: Array.isArray(appointmentsRes.data)
            ? appointmentsRes.data
            : appointmentsRes.data?.items || [],
          invoices: Array.isArray(invoicesRes.data) ? invoicesRes.data : invoicesRes.data?.items || [],
          payments: Array.isArray(paymentsRes.data) ? paymentsRes.data : paymentsRes.data?.items || [],
        });
      } catch (e) {
        if (requestId !== patientBundleRequestRef.current) return;
        setAppointmentLoad({ patientId, loading: false, error: 'Não foi possível carregar os dados do paciente. Tente novamente.' });
        console.error('Erro ao carregar dados do paciente:', e);
        showError('Erro ao carregar dados do paciente');
      }
    },
    [showError, can]
  );

  // Recarregar bundles quando muda paciente
  useEffect(() => {
    if (mode === 'detail' && selectedPatient?.id) {
      loadPatientBundles(selectedPatient.id);
    }
  }, [mode, selectedPatient?.id, loadPatientBundles]);

  useEffect(() => {
    if (activeTab === 'timeline' && selectedPatient?.id) {
      loadAuditHistory(false);
    }
  }, [activeTab, selectedPatient?.id, loadAuditHistory]);

  // Ler contexto de atendimento pelo localStorage (fluxo atual da Agenda)
  useEffect(() => {
    let cancelled = false;

    const restoreActiveAppointment = async () => {
      try {
        const stored = localStorage.getItem('activeAppointment');
        if (stored) {
          const obj = JSON.parse(stored);
          if (!obj?.id || obj?.patient_id !== selectedPatient?.id) {
            setActiveAppt(null);
            return;
          }

          const { data: appointment } = await axios.get(
            `${API_URL}/appointments/${obj.id}`,
            { headers: headersAuth }
          );

          if (cancelled) return;

          if (!canStartAppointment(appointment)) {
            localStorage.removeItem('activeAppointment');
            setActiveAppt(null);
            return;
          }

          setActiveAppt(appointment);
          if (appointment?.patient_id === selectedPatient?.id) {
            setActiveTab('appointments');
          }
        }
      } catch {
        if (!cancelled) {
          localStorage.removeItem('activeAppointment');
          setActiveAppt(null);
        }
      }
    };

    restoreActiveAppointment();
    return () => { cancelled = true; };
  }, [selectedPatient?.id]);

  // Ler contexto de atendimento pela URL (fluxo da extensão)
  useEffect(() => {
    const activeApptQS = getQS('activeAppt');
    const tabQS = getQS('tab');

    if (!selectedPatient?.id) return;

    let cancelled = false;

    if (activeApptQS) {
      const validateUrlAppointment = async () => {
        try {
          const { data: appointment } = await axios.get(
            `${API_URL}/appointments/${activeApptQS}`,
            { headers: headersAuth }
          );

          if (cancelled) return;

          if (
            appointment?.patient_id !== selectedPatient.id ||
            !canStartAppointment(appointment)
          ) {
            localStorage.removeItem('activeAppointment');
            setActiveAppt(null);
            return;
          }

          localStorage.setItem('activeAppointment', JSON.stringify(appointment));
          setActiveAppt(appointment);
          setActiveTab(isValidTab(tabQS) ? tabQS : 'appointments');
        } catch {
          if (!cancelled) {
            localStorage.removeItem('activeAppointment');
            setActiveAppt(null);
          }
        }
      };

      validateUrlAppointment();
      return () => { cancelled = true; };
    }

    if (isValidTab(tabQS)) {
      setActiveTab(tabQS);
    }

    return () => { cancelled = true; };
  }, [selectedPatient?.id]);

  const handleAppointmentCompleted = useCallback(() => {
    if (getQS('from') === 'agenda') {
      window.location.href = '/agenda';
    }
  }, []);

  // ====== Ações do PatientForm ======
  const handleNewPatient = () => {
    setPfIsOpen(true);
    setPfIsEditing(false);
    setPfFormData({
      full_name: '',
      email: '',
      phone: '',
      birth_date: '',
      cpf: '',
      address: '',
      notes: '',
      address_street: '',
      address_number: '',
      address_complement: '',
      address_neighborhood: '',
      address_city: '',
      address_state: '',
      address_cep: '',
      preferred_name: '',
      accessibility_needs: '',
      advance_directives: '',
      authorized_contact_name: '',
      authorized_contact_phone: '',
      authorized_contact_relationship: '',      
    });
  };

  const handleEditPatient = () => {
    if (!selectedPatient) return;
    setPfIsOpen(true);
    setPfIsEditing(true);
    setPfFormData({
      id: selectedPatient.id,
      full_name: selectedPatient.full_name || '',
      email: selectedPatient.email || '',
      phone: selectedPatient.phone || '',
      birth_date: selectedPatient.birth_date || '',
      cpf: selectedPatient.cpf || '',
      rg: selectedPatient.rg || '',
      gender: normalizePatientGender(selectedPatient.gender),
      marital_status: normalizePatientMaritalStatus(selectedPatient.marital_status || selectedPatient.civil_status),
      profession: selectedPatient.profession || '',      
      address: selectedPatient.address || '',
      notes: selectedPatient.notes || '',
      address_street: selectedPatient.address_street || '',
      address_number: selectedPatient.address_number || '',
      address_complement: selectedPatient.address_complement || '',
      address_neighborhood: selectedPatient.address_neighborhood || '',
      address_city: selectedPatient.address_city || '',
      address_state: selectedPatient.address_state || '',
      address_cep: selectedPatient.address_cep || '',

      is_minor_or_dependent: !!selectedPatient.is_minor_or_dependent,
      legal_guardian_type: selectedPatient.legal_guardian_type || '',
      legal_guardian_name: selectedPatient.legal_guardian_name || '',
      legal_guardian_cpf: selectedPatient.legal_guardian_cpf || '',
      legal_guardian_phone: selectedPatient.legal_guardian_phone || '',
      legal_guardian_relationship: selectedPatient.legal_guardian_relationship || '',
      legal_guardian_basis: selectedPatient.legal_guardian_basis || '',
      legal_guardian_notes: selectedPatient.legal_guardian_notes || '',
      emergency_contact_name: selectedPatient.emergency_contact_name || '',
      emergency_contact_phone: selectedPatient.emergency_contact_phone || '',
      clinical_alerts: selectedPatient.clinical_alerts || '',
      administrative_notes: selectedPatient.administrative_notes || '',

      preferred_name: selectedPatient.preferred_name || '',
      accessibility_needs: selectedPatient.accessibility_needs || '',
      advance_directives: selectedPatient.advance_directives || '',
      authorized_contact_name: selectedPatient.authorized_contact_name || '',
      authorized_contact_phone: selectedPatient.authorized_contact_phone || '',
      authorized_contact_relationship: selectedPatient.authorized_contact_relationship || '',
    });
  };

  const handleSavePatient = async (formData) => {
    try {
      if (pfIsEditing && formData.id) {
        await axios.put(`${API_URL}/patients/${formData.id}`, formData, { headers: headersAuth });
        showSuccess('Paciente atualizado com sucesso!');
      } else {
        await axios.post(`${API_URL}/patients`, formData, { headers: headersAuth });
        showSuccess('Paciente criado com sucesso!');
      }

      setPfIsOpen(false);
      const list = await fetchPatients();
      if (pfIsEditing && formData.id) {
        const found = list.find((p) => p.id === formData.id);
        if (found) {
          setSelectedPatient(found);
          localStorage.setItem('currentPatientId', found.id);
          setMode('detail');
        }
      }
    } catch (e) {
      console.error('Erro ao salvar paciente:', e);
      showError('Erro ao salvar paciente');
    }
  };

  const refreshSelectedPatient = async (patientId) => {
    if (!patientId) return null;

    const { data } = await axios.get(`${API_URL}/patients/${patientId}`, {
      headers: headersAuth,
    });

    setSelectedPatient(data);
    setPatients((prev) =>
      prev.map((p) => (p.id === data.id ? data : p))
    );

    return data;
  };

  const clearPatientPhotoObjectUrl = useCallback(() => {
    if (patientPhotoObjectUrlRef.current) {
      URL.revokeObjectURL(patientPhotoObjectUrlRef.current);
      patientPhotoObjectUrlRef.current = null;
    }

    setPatientPhotoUrl(null);
  }, []);

  const loadPatientPhoto = useCallback(
    async (patientId, { showLoading = true, version = null } = {}) => {
      if (!patientId) {
        patientPhotoRequestIdRef.current += 1;
        clearPatientPhotoObjectUrl();
        return null;
      }

      const requestId = patientPhotoRequestIdRef.current + 1;
      patientPhotoRequestIdRef.current = requestId;

      try {
        if (showLoading) {
          setPatientPhotoLoading(true);
        }

        const cacheVersion = version || Date.now();
        const response = await axios.get(
          `${API_URL}/patients/${patientId}/photo?v=${encodeURIComponent(cacheVersion)}`,
          {
            headers: headersAuth,
            responseType: 'blob',
          }
        );

        const objectUrl = URL.createObjectURL(response.data);

        // Ignora respostas antigas quando o paciente foi trocado ou uma nova
        // solicitação foi iniciada antes da conclusão desta.
        if (requestId !== patientPhotoRequestIdRef.current) {
          URL.revokeObjectURL(objectUrl);
          return null;
        }

        if (patientPhotoObjectUrlRef.current) {
          URL.revokeObjectURL(patientPhotoObjectUrlRef.current);
        }

        patientPhotoObjectUrlRef.current = objectUrl;
        setPatientPhotoUrl(objectUrl);

        return objectUrl;
      } catch (error) {
        if (requestId === patientPhotoRequestIdRef.current) {
          clearPatientPhotoObjectUrl();
        }

        if (error?.response?.status !== 404) {
          console.error('Erro ao carregar foto do paciente:', error);
        }

        return null;
      } finally {
        if (showLoading && requestId === patientPhotoRequestIdRef.current) {
          setPatientPhotoLoading(false);
        }
      }
    },
    [clearPatientPhotoObjectUrl]
  );

  useEffect(() => {
    if (mode !== 'detail' || !selectedPatient?.id) {
      clearPatientPhotoObjectUrl();
      return undefined;
    }

    loadPatientPhoto(selectedPatient.id, {
      version: selectedPatient.photo_updated_at,
    });

    return undefined;
  }, [
    mode,
    selectedPatient?.id,
    selectedPatient?.photo_updated_at,
    loadPatientPhoto,
    clearPatientPhotoObjectUrl,
  ]);

  useEffect(() => {
    return () => {
      if (patientPhotoObjectUrlRef.current) {
        URL.revokeObjectURL(patientPhotoObjectUrlRef.current);
        patientPhotoObjectUrlRef.current = null;
      }
    };
  }, []);

  const handleUploadPatientPhoto = async (event) => {
    const file = event.target.files?.[0];

    if (!file || !selectedPatient?.id) return;

    try {
      setPatientPhotoUploading(true);

      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axios.post(
        `${API_URL}/patients/${selectedPatient.id}/photo`,
        formData,
        {
          headers: {
            ...headersAuth,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      setSelectedPatient(data);
      setPatients((prev) =>
        prev.map((p) => (p.id === data.id ? data : p))
      );

      await loadPatientPhoto(selectedPatient.id, {
        showLoading: false,
        version: data.photo_updated_at,
      });

      showSuccess('Foto do paciente atualizada com sucesso.');
    } catch (error) {
      console.error('Erro ao enviar foto do paciente:', error);
      showError(error?.response?.data?.detail || 'Erro ao enviar foto do paciente.');
    } finally {
      setPatientPhotoUploading(false);

      if (photoInputRef.current) {
        photoInputRef.current.value = '';
      }
    }
  };

  const handleRemovePatientPhoto = async () => {
    if (!selectedPatient?.id) return;

    const confirmRemove = window.confirm('Remover a foto do paciente?');
    if (!confirmRemove) return;

    try {
      setPatientPhotoUploading(true);

      const { data } = await axios.delete(
        `${API_URL}/patients/${selectedPatient.id}/photo`,
        { headers: headersAuth }
      );

      setSelectedPatient(data);
      setPatients((prev) =>
        prev.map((p) => (p.id === data.id ? data : p))
      );
      clearPatientPhotoObjectUrl();

      showSuccess('Foto removida com sucesso.');
    } catch (error) {
      console.error('Erro ao remover foto do paciente:', error);
      showError('Erro ao remover foto do paciente.');
    } finally {
      setPatientPhotoUploading(false);
    }
  };

  const handleRefreshPatientPhoto = async () => {
    if (!selectedPatient?.id) return;

    try {
      setRefreshingPatientPhoto(true);

      const refreshedPatient = await refreshSelectedPatient(selectedPatient.id);
      await loadPatientPhoto(selectedPatient.id, {
        showLoading: false,
        version: refreshedPatient?.photo_updated_at,
      });

      showSuccess('Foto do paciente atualizada.');
    } catch (error) {
      console.error('Erro ao atualizar foto do paciente:', error);
      showError('Erro ao atualizar a foto.');
    } finally {
      setRefreshingPatientPhoto(false);
    }
  };

  const handleCreatePatientPhotoLink = async () => {
    if (!selectedPatient?.id) return;

    try {
      setPatientPhotoLinkLoading(true);

      const { data } = await axios.post(
        `${API_URL}/patients/${selectedPatient.id}/photo-link`,
        {},
        { headers: headersAuth }
      );

      const publicUrl = `${window.location.origin}${data.path}`;

      await navigator.clipboard.writeText(publicUrl);

      showSuccess('Link para envio da foto copiado. O link expira em 7 dias.');
    } catch (error) {
      console.error('Erro ao gerar link de foto:', error);
      showError('Erro ao gerar link público para envio da foto.');
    } finally {
      setPatientPhotoLinkLoading(false);
    }
  };

  const handleSendPatientPhotoLinkWhatsApp = async () => {
    if (!selectedPatient?.id) return;

    const confirmSend = window.confirm(
      `Enviar link para ${selectedPatient.full_name} tirar/enviar a foto por WhatsApp?`
    );

    if (!confirmSend) return;

    try {
      setSendingPhotoLinkWhatsApp(true);

      const response = await axios.post(
        `${API_URL}/patients/${selectedPatient.id}/photo-link-whatsapp`,
        {},
        { headers: headersAuth }
      );

      showSuccess(response.data?.message || 'Link para envio da foto enviado por WhatsApp.');
    } catch (error) {
      console.error('Erro ao enviar link de foto por WhatsApp:', error);

      const msg =
        error.response?.data?.detail ||
        'Erro ao enviar link para envio da foto por WhatsApp.';

      showError(msg);
      window.alert(msg);
    } finally {
      setSendingPhotoLinkWhatsApp(false);
    }
  };
  
  const handleSelectPatient = async (patient) => {
    const targetTab = dashboardFilter === 'stalled' ? 'treatments' : 'overview';

    try {
      localStorage.setItem('currentPatientId', patient.id);
      setMode('detail');
      setActiveTab(targetTab);

      const url = new URL(window.location.href);
      url.searchParams.delete('modo');
      url.searchParams.set('patientId', patient.id);
      url.searchParams.set('tab', targetTab);
      window.history.replaceState({}, '', url.toString());

      const fullPatient = await refreshSelectedPatient(patient.id);

      setSelectedPatient(fullPatient || patient);
    } catch (error) {
      console.error('Erro ao carregar paciente completo:', error);

      setSelectedPatient(patient);
      localStorage.setItem('currentPatientId', patient.id);
      setMode('detail');
      setActiveTab(targetTab);
    }
  };

  const handleBackToList = () => {
    localStorage.removeItem("support_active_tab");
    setSelectedPatient(null);
    localStorage.removeItem('currentPatientId');
    setMode('list');
  };

  const isMobileLike =
    /Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    window.matchMedia('(max-width: 768px)').matches;

  const openWhatsAppWeb = async (patient = null) => {
    const patientId = patient?.id || '';
    const patientName = patient?.full_name || '';
    const patientPhone = patient?.phone || '';
    const normalizedPhone = String(patientPhone).replace(/\D/g, '');

    if (isMobileLike) {
      if (normalizedPhone) {
        window.location.href = `https://wa.me/${normalizedPhone}`;
      } else {
        showInfo('No celular, este atalho só funciona quando o paciente tiver telefone cadastrado.');
      }
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const params = new URLSearchParams();
      params.set('source', 'prontuarios');
      if (patientId) params.set('patient_id', patientId);
      if (patientName) params.set('patient_name', patientName);
      if (patientPhone) params.set('patient_phone', patientPhone);

      const response = await fetch(`${API_URL}/extension/session-ticket?${params.toString()}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();
      if (!response.ok || !data?.ticket) {
        console.error('Erro ao gerar ticket da extensão:', data);
        return;
      }

      const url = new URL('https://web.whatsapp.com');
      url.searchParams.set('odontoassist_source', 'prontuarios');
      if (patientId) url.searchParams.set('patientId', patientId);
      if (patientName) url.searchParams.set('patientName', patientName);
      if (patientPhone) url.searchParams.set('patientPhone', patientPhone);
      url.searchParams.set('ext_ticket', data.ticket);

      window.open(url.toString(), '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Erro ao abrir integração com WhatsApp:', err);
    }
  };

  const hasWhatsAppModuleForActions =
    clinicSettingsForActions?.whatsapp_module_access_active === true ||
    clinicSettingsForActions?.whatsapp_module_status === 'active' ||
    clinicSettingsForActions?.whatsapp_module_status === 'courtesy' ||
    clinicSettingsForActions?.whatsapp_module_status === 'cancel_pending' ||
    clinicSettingsForActions?.whatsapp_module_enabled === true ||
    user?.whatsapp_module_enabled === true ||
    user?.whatsapp_module_courtesy === true;

  const canSendOnlineBookingWhatsapp =
    hasWhatsAppModuleForActions &&
    clinicSettingsForActions?.online_booking_enabled &&
    clinicSettingsForActions?.online_booking_token &&
    selectedPatient?.id;

  const handleSendOnlineBookingLinkWhatsApp = async () => {
    if (!selectedPatient?.id) return;

    const confirmSend = window.confirm(
      `Enviar link de autoagendamento por WhatsApp para ${selectedPatient.full_name}?`
    );

    if (!confirmSend) return;

    try {
      setSendingOnlineBookingWhatsApp(true);

      const response = await axios.post(
        `${API_URL}/appointments/patients/${selectedPatient.id}/send-online-booking-link-whatsapp`,
        {},
        { headers: headersAuth }
      );

      showSuccess(response.data?.message || 'Link de autoagendamento enviado por WhatsApp.');
    } catch (error) {
      console.error('Erro ao enviar link de autoagendamento:', error);

      const msg =
        error.response?.data?.detail ||
        'Erro ao enviar link de autoagendamento por WhatsApp.';

      showError(msg);
      window.alert(msg);
    } finally {
      setSendingOnlineBookingWhatsApp(false);
    }
  };

  const translateExportPurpose = (value) => {
    const map = {
      uso_interno_auditoria: 'Uso interno / auditoria',
      solicitacao_paciente: 'Solicitação do paciente',
      solicitacao_responsavel_representante: 'Solicitação de responsável/representante',
      encaminhamento_outro_profissional: 'Encaminhamento para outro profissional',
      solicitacao_judicial: 'Solicitação judicial',
      outro: 'Outro',
    };

    return map[value] || value || '—';
  };

  const renderAuditDetails = (details = {}) => {
    if (!details || typeof details !== 'object') return null;

    const rows = [];

    if (details.title) rows.push(['Documento', details.title]);
    if (details.file_name) rows.push(['Arquivo', details.file_name]);
    if (details.finalidade) rows.push(['Finalidade', translateExportPurpose(details.finalidade)]);
    if (details.solicitante) rows.push(['Solicitante/recebedor', details.solicitante]);
    if (details.observacoes) rows.push(['Observações', details.observacoes]);
    if (details.hash) rows.push(['Hash SHA-256', details.hash]);
    if (details.generated_at) rows.push(['Gerado em', formatDateTimeBR(details.generated_at)]);
    if (details.reason) rows.push(['Motivo', details.reason]);
    if (details.from) rows.push(['Documento de origem', details.from]);

    if (details.amount !== undefined) rows.push(['Valor', formatCurrency(details.amount)]);
    if (details.balance !== undefined) rows.push(['Saldo', formatCurrency(details.balance)]);
    if (details.invoice) rows.push(['Fatura', `#${details.invoice}`]);
    if (details.origin) rows.push(['Origem', details.origin]);

    if (!rows.length) {
      return Object.entries(details).map(([key, value]) => (
        <div key={key}>
          <strong>{key}:</strong> {String(value)}
        </div>
      ));
    }

    return rows.map(([label, value]) => (
      <div key={label} className={label.includes('Hash') ? 'break-all' : ''}>
        <strong>{label}:</strong> {String(value || '—')}
      </div>
    ));
  };

  const mergeTargetCandidates = useMemo(() => {
    const query = mergeTargetSearch.trim().toLocaleLowerCase('pt-BR');
    if (!mergeSourcePatient || query.length < 2) return [];
    return patients
      .filter((patient) => patient.id !== mergeSourcePatient.id)
      .filter((patient) => {
        if (patient.is_inactive || patient.merged_into_patient_id) return false;
        const searchable = `${patient.full_name || ''} ${patient.cpf || ''} ${patient.phone || ''}`
          .toLocaleLowerCase('pt-BR');
        return searchable.includes(query);
      })
      .slice(0, 8);
  }, [mergeSourcePatient, mergeTargetSearch, patients]);

  useEffect(() => {
    let mounted = true;
    if (!mergeSourcePatient?.id || !mergeTargetPatient?.id) {
      setMergePreview(null);
      setMergeIdentityConfirmed(false);
      return undefined;
    }

    const loadPreview = async () => {
      setLoadingMergePreview(true);
      setMergeFieldChoices({});
      setMergeIdentityConfirmed(false);
      try {
        const { data } = await axios.get(
          `${API_URL}/patients/${mergeSourcePatient.id}/merge-empty-preview`,
          {
            params: { target_patient_id: mergeTargetPatient.id },
            headers: headersAuth,
          }
        );
        if (mounted) setMergePreview(data);
      } catch (error) {
        if (mounted) {
          setMergePreview(null);
          showError(error?.response?.data?.detail || 'Não foi possível conferir os prontuários.');
        }
      } finally {
        if (mounted) setLoadingMergePreview(false);
      }
    };

    loadPreview();
    return () => {
      mounted = false;
    };
  }, [mergeSourcePatient?.id, mergeTargetPatient?.id]);

  const closeMergePatient = (force = false) => {
    if (mergingPatient && !force) return;
    setMergeSourcePatient(null);
    setMergeTargetPatient(null);
    setMergeTargetSearch('');
    setMergePreview(null);
    setMergeFieldChoices({});
    setMergeIdentityConfirmed(false);
  };

  const mergeConflicts = mergePreview?.conflicts || [];
  const allMergeConflictsResolved = mergeConflicts.every(
    (conflict) => ['source', 'target'].includes(mergeFieldChoices[conflict.field])
  );

  const formatMergeFieldValue = (field, value) => {
    if (value === null || value === undefined || value === '') return 'Não informado';
    if (field === 'birth_date') return formatDate(value);
    if (field === 'cpf' || field === 'legal_guardian_cpf') return formatCpfDisplay(value);
    if (['phone', 'legal_guardian_phone', 'emergency_contact_phone'].includes(field)) {
      return formatPhoneDisplay(value);
    }
    return String(value);
  };

  const handleMergeEmptyPatient = async () => {
    if (
      !mergeSourcePatient?.id ||
      !mergeTargetPatient?.id ||
      !mergePreview?.can_merge ||
      !mergeIdentityConfirmed ||
      !allMergeConflictsResolved
    ) return;
    const confirmed = window.confirm(
      `Confirma a união de “${mergeSourcePatient.full_name}” com “${mergeTargetPatient.full_name}”? Todos os registros serão transferidos para o prontuário principal, que será mantido.`
    );
    if (!confirmed) return;

    try {
      setMergingPatient(true);
      const { data } = await axios.post(
        `${API_URL}/patients/${mergeSourcePatient.id}/merge-empty`,
        {
          target_patient_id: mergeTargetPatient.id,
          field_choices: mergeFieldChoices,
          same_person_confirmed: true,
        },
        { headers: headersAuth }
      );
      showSuccess(data?.message || 'Prontuário duplicado unido com sucesso.');
      closeMergePatient(true);
      await fetchPatients();
    } catch (error) {
      showError(error?.response?.data?.detail || 'Não foi possível unir os prontuários.');
    } finally {
      setMergingPatient(false);
    }
  };

  const recordTabs = [
    ['overview', 'Visão Geral', <FileText className="w-4 h-4 mr-2" />],
    ['timeline', 'Timeline', <ShieldCheck className="w-4 h-4 mr-2" />],
    ['odontogram', 'Odontograma', <Grid className="w-4 h-4 mr-2" />],
    ['treatments', 'Tratamentos', <Smile className="w-4 h-4 mr-2" />],
    ['evolution', 'Evolução Clínica', <Activity className="w-4 h-4 mr-2" />],
    ['aligners', 'Alinhadores', <Smile className="w-4 h-4 mr-2" />],
    ['appointments', 'Agendamentos', <Calendar className="w-4 h-4 mr-2" />],
    ['analyses', 'Histórico da IA', <Brain className="w-4 h-4 mr-2" />],
    ['images', 'Imagens', <ImageIcon className="w-4 h-4 mr-2" />],
    ['documents', 'Documentos', <Eye className="w-4 h-4 mr-2" />],
    ['anamnese', 'Anamnese', <Users className="w-4 h-4 mr-2" />],
    ['financial', 'Financeiro', <DollarSign className="w-4 h-4 mr-2" />],
  ];
  const recordTabPermission = {
    overview: 'patients.view', timeline: 'audit.view',
    odontogram: 'clinical_records.view', treatments: 'clinical_records.view',
    evolution: 'clinical_records.view', aligners: 'aligners.view',
    appointments: 'appointments.view', analyses: 'ai.clinical_use',
    images: 'images.view', documents: 'documents.view',
    anamnese: 'anamnese.view', financial: 'billing.view',
  };
  const visibleRecordTabs = recordTabs
    .filter(([key]) => key !== 'aligners' || hasAlignersModule)
    .filter(([key]) => !recordTabPermission[key] || can(recordTabPermission[key]));
  const primaryRecordTabs = visibleRecordTabs.filter(
    ([key]) => !ADDITIONAL_RECORD_TAB_IDS.has(key)
  );
  const additionalRecordTabs = visibleRecordTabs.filter(
    ([key]) => ADDITIONAL_RECORD_TAB_IDS.has(key)
  );
  const renderRecordTab = ([key, label, icon]) => (
    <button
      key={key}
      type="button"
      onClick={() => setActiveTab(key)}
      className={`-mb-px flex items-center border-b-2 px-4 py-3 text-sm font-medium ${
        activeTab === key
          ? 'border-purple-600 text-purple-600'
          : 'border-transparent text-gray-600 hover:text-gray-800'
      }`}
    >
      {icon}
      {label}
    </button>
  );

  return (
    <Layout title="Prontuários">
      {generatedReceiptId && (
        <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="font-medium text-emerald-800">
                Recibo de entrega criado com sucesso
              </div>

              <div className="text-sm text-emerald-700">
                O PDF foi gerado e o recibo já está disponível para assinatura,
                envio por link ou impressão.
              </div>
            </div>

            <button
              onClick={() => {
                setReceiptToOpenId(generatedReceiptId);
                setActiveTab('documents');
                setGeneratedReceiptId(null);
              }}
              className="px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
            >
              Abrir recibo
            </button>
          </div>
        </div>
      )}    
      {exportingProntuario && (
        <div className="fixed inset-0 z-[9999] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md border">
            <div className="flex items-center gap-3 mb-3">
              <RefreshCw className="w-5 h-5 animate-spin text-purple-600" />
              <div>
                <h3 className="font-semibold text-gray-800">
                  Gerando prontuário
                </h3>
                <p className="text-sm text-gray-600">
                  Isso pode levar até 1 minuto, dependendo do volume de dados.
                </p>
              </div>
            </div>

            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div className="h-2 bg-purple-600 rounded-full animate-pulse w-2/3" />
            </div>

            <p className="text-xs text-gray-500 mt-3">
              Não feche esta página até o download começar.
            </p>
          </div>
        </div>
      )}      
      {loading && (
        <div className="p-6 text-center text-gray-600">Carregando prontuários…</div>
      )}

      {!loading && (
        <div className="p-4 space-y-4">
          {mode === 'list' && (
            <>
              <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                <div>
                  <h1 className="text-xl font-semibold text-gray-800">Pacientes</h1>
                  <p className="text-sm text-gray-500">
                    Selecione um paciente para visualizar o prontuário completo.
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openWhatsAppWeb()}
                    className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"
                  >
                    <MessageCircle className="w-4 h-4 mr-1" />
                    Abrir WhatsApp Web
                  </button>

                  {can('patients.create') && <button
                    data-onboarding-id="new-patient"
                    onClick={handleNewPatient}
                    className="inline-flex items-center px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Novo Paciente
                  </button>}
                </div>
              </div>

              <PatientStatusFilter value={patientStatus} onChange={value => { setPatientStatus(value); clearDashboardFilter(); }} />
              {dashboardFilter && (
                <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-semibold text-purple-900">
                        {dashboardFilter === 'inactive'
                          ? 'Filtro aplicado: pacientes sem retorno'
                          : 'Filtro aplicado: pacientes com tratamentos parados'}
                      </p>
                      <p className="mt-1 text-sm text-purple-700">
                        {loadingDashboardFilter
                          ? 'Analisando os dados da clínica...'
                          : `${dashboardFilterPatientIds.length} paciente(s) encontrado(s).`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={clearDashboardFilter}
                      className="inline-flex items-center justify-center rounded-lg border border-purple-300 bg-white px-3 py-2 text-sm font-semibold text-purple-700 hover:bg-purple-100"
                    >
                      <XCircle className="mr-2 h-4 w-4" />
                      Limpar filtro
                    </button>
                  </div>
                </div>
              )}

              <div className="bg-white p-5 rounded-lg border border-gray-200">
                <div className="relative">
                  <Search className="w-5 h-5 absolute left-3 top-3 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar por nome, e-mail ou telefone…"
                    value={patientSearch}
                    onChange={(e) => setPatientSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {loadingDashboardFilter ? (
                  <div className="p-12 text-center text-gray-600">
                    Aplicando filtro do Dashboard...
                  </div>
                ) : filteredPatients.length === 0 ? (
                  <div className="p-12 text-center">
                    <p className="font-medium text-gray-800">
                      {dashboardFilter
                        ? 'Nenhum paciente atende aos critérios deste filtro.'
                        : 'Nenhum paciente encontrado.'}
                    </p>
                    {dashboardFilter && (
                      <button
                        type="button"
                        onClick={clearDashboardFilter}
                        className="mt-3 text-sm font-semibold text-purple-600 hover:text-purple-700"
                      >
                        Mostrar todos os pacientes
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paciente</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contato</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nascimento</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cadastro</th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ações</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredPatients.map((p) => (
                          <tr key={p.id}>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{p.full_name}{p.is_inactive && <span className="ml-2 text-xs text-amber-700">Inativo</span>}</div>
                              {dashboardFilterDetails[String(p?.id || p?._id || '')]?.label && (
                                <div className="mt-1 text-xs font-medium text-purple-700">
                                  {dashboardFilterDetails[String(p?.id || p?._id || '')].label}
                                </div>
                              )}
                              {p.cpf && (
                                <div className="text-xs text-gray-500">CPF: {formatCpfDisplay(p.cpf)}</div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{formatPhoneDisplay(p.phone)}</div>
                              <div className="text-xs text-gray-500">{p.email || '—'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {p.birth_date ? formatDate(p.birth_date) : '—'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {p.created_at ? formatDate(p.created_at) : '—'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                              <button
                                onClick={() => handleSelectPatient(p)}
                                className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
                              >
                                <Eye className="w-4 h-4 mr-1" />
                                Ver Prontuário
                              </button>
                              {can('patients.update') && <button
                                onClick={() => {
                                  setPfIsEditing(true);
                                  setPfFormData({
                                    id: p.id,
                                    full_name: p.full_name || '',
                                    email: p.email || '',
                                    phone: p.phone || '',
                                    birth_date: p.birth_date || '',
                                    cpf: p.cpf || '',
                                    address: p.address || '',
                                    notes: p.notes || '',

                                    rg: p.rg || '',
                                    gender: normalizePatientGender(p.gender),
                                    marital_status: normalizePatientMaritalStatus(p.marital_status || p.civil_status),
                                    profession: p.profession || '',                                 

                                    address_street: p.address_street || '',
                                    address_number: p.address_number || '',
                                    address_complement: p.address_complement || '',
                                    address_neighborhood: p.address_neighborhood || '',
                                    address_city: p.address_city || '',
                                    address_state: p.address_state || '',
                                    address_cep: p.address_cep || '',

                                    is_minor_or_dependent: !!p.is_minor_or_dependent,
                                    legal_guardian_type: p.legal_guardian_type || '',
                                    legal_guardian_name: p.legal_guardian_name || '',
                                    legal_guardian_cpf: p.legal_guardian_cpf || '',
                                    legal_guardian_phone: p.legal_guardian_phone || '',
                                    legal_guardian_relationship: p.legal_guardian_relationship || '',
                                    legal_guardian_basis: p.legal_guardian_basis || '',
                                    legal_guardian_notes: p.legal_guardian_notes || '',
                                    emergency_contact_name: p.emergency_contact_name || '',
                                    emergency_contact_phone: p.emergency_contact_phone || '',
                                    clinical_alerts: p.clinical_alerts || '',
                                    administrative_notes: p.administrative_notes || '',

                                    preferred_name: p.preferred_name || '',
                                    accessibility_needs: p.accessibility_needs || '',
                                    advance_directives: p.advance_directives || '',
                                    authorized_contact_name: p.authorized_contact_name || '',
                                    authorized_contact_phone: p.authorized_contact_phone || '',
                                    authorized_contact_relationship: p.authorized_contact_relationship || '',                                    
                                  });
                                  setPfIsOpen(true);
                                }}
                                className="inline-flex items-center px-3 py-1.5 rounded-md border border-blue-500 text-xs text-blue-600 hover:bg-blue-50"
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Editar
                              </button>}
                              {!p.is_inactive && can('patients.update') && can('patients.inactivate') && <button
                                type="button"
                                onClick={() => {
                                  setMergeSourcePatient(p);
                                  setMergeTargetPatient(null);
                                  setMergeTargetSearch('');
                                  setMergePreview(null);
                                }}
                                className="inline-flex items-center px-3 py-1.5 rounded-md border border-amber-400 text-xs text-amber-700 hover:bg-amber-50"
                                title="Unir este cadastro a outro prontuário da mesma pessoa"
                              >
                                <GitMerge className="w-4 h-4 mr-1" />
                                Mesclar duplicado
                              </button>}
                              <PatientStatusAction patient={p} onChanged={async updated => {
                                setPatients(prev => prev.map(item => item.id === updated.id ? updated : item));
                                if (selectedPatient?.id === updated.id) setSelectedPatient(updated);
                              }} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <PatientForm
                isOpen={pfIsOpen}
                onClose={() => setPfIsOpen(false)}
                onSubmit={handleSavePatient}
                formData={pfFormData}
                setFormData={setPfFormData}
                isEditing={pfIsEditing}
                title={pfIsEditing ? 'Editar Paciente' : 'Novo Paciente'}
                canEditClinicalData={can('clinical_records.update')}
              />
            </>
          )}

          {mode === 'detail' && selectedPatient && (
            <div className="space-y-4">
              <button
                onClick={handleBackToList}
                className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                Voltar para lista de pacientes
              </button>

              <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {patientPhotoUrl ? (
                      <img
                        src={patientPhotoUrl}
                        alt={`Foto de ${selectedPatient.full_name}`}
                        className="w-20 h-20 rounded-2xl object-cover border border-gray-200 shadow-sm bg-gray-50"
                      />
                    ) : (
                      <div className="w-20 h-20 rounded-2xl border border-dashed border-gray-300 bg-gray-50 flex items-center justify-center">
                        {patientPhotoLoading ? (
                          <RefreshCw className="w-7 h-7 text-gray-400 animate-spin" />
                        ) : (
                          <UserRound className="w-9 h-9 text-gray-400" />
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="text-xs uppercase text-gray-500 mb-1">{selectedPatient.is_inactive ? 'Paciente inativo' : 'Paciente em atendimento'}</div>
                    <PatientInactiveNotice patient={selectedPatient} />
                    <PatientStatusAction key={selectedPatient.id} patient={selectedPatient} onChanged={updated => {
                      setSelectedPatient(updated);
                      setPatients(prev => prev.map(item => item.id === updated.id ? updated : item));
                    }} />
                    <div className="text-lg font-semibold text-gray-900">{selectedPatient.full_name}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {selectedPatient.cpf && <span className="mr-3">CPF: {formatCpfDisplay(selectedPatient.cpf)}</span>}
                      {selectedPatient.birth_date && (
                        <span>
                          Nasc.: {formatDate(selectedPatient.birth_date)}
                        </span>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 mt-3">
                      <div className="flex flex-wrap gap-2">
                        <input
                          ref={photoInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          className="hidden"
                          onChange={handleUploadPatientPhoto}
                        />

                        {can('patients.update') && (isMobileOrTablet ? (
                          <>
                            <label className="inline-flex items-center px-3 py-1.5 rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50 text-xs cursor-pointer">
                              <Camera className="w-4 h-4 mr-1" />
                              Tirar foto
                              <input
                                type="file"
                                accept="image/*"
                                capture="environment"
                                className="hidden"
                                onChange={handleUploadPatientPhoto}
                              />
                            </label>

                            <button
                              type="button"
                              onClick={() => photoInputRef.current?.click()}
                              disabled={patientPhotoUploading}
                              className="inline-flex items-center px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs disabled:opacity-50"
                            >
                              <Upload className="w-4 h-4 mr-1" />
                              Escolher foto
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            onClick={() => photoInputRef.current?.click()}
                            disabled={patientPhotoUploading}
                            className="inline-flex items-center px-3 py-1.5 rounded-md border border-blue-300 text-blue-700 hover:bg-blue-50 text-xs disabled:opacity-50"
                          >
                            <Upload className="w-4 h-4 mr-1" />
                            {patientPhotoUrl ? 'Alterar foto' : 'Adicionar foto'}
                          </button>
                        ))}

                        {can('patients.update') && <button
                          type="button"
                          onClick={handleCreatePatientPhotoLink}
                          disabled={patientPhotoLinkLoading}
                          className="inline-flex items-center px-3 py-1.5 rounded-md border border-purple-300 text-purple-700 hover:bg-purple-50 text-xs disabled:opacity-50"
                        >
                          <LinkIcon className="w-4 h-4 mr-1" />
                          Copiar link para paciente tirar sua foto
                        </button>}

                        {can('whatsapp.send') && hasWhatsAppModuleForActions && selectedPatient?.phone && (
                          <button
                            type="button"
                            onClick={handleSendPatientPhotoLinkWhatsApp}
                            disabled={sendingPhotoLinkWhatsApp}
                            className="inline-flex items-center px-3 py-1.5 rounded-md border border-green-300 text-green-700 hover:bg-green-50 text-xs disabled:opacity-50"
                            title="Enviar link para o paciente tirar/enviar a foto por WhatsApp"
                          >
                            <MessageCircle className="w-4 h-4 mr-1" />
                            {sendingPhotoLinkWhatsApp
                              ? 'Enviando...'
                              : 'Enviar link da foto por WhatsApp'}
                          </button>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2">
                        {can('images.delete') && patientPhotoUrl && (
                          <button
                            type="button"
                            onClick={handleRemovePatientPhoto}
                            disabled={patientPhotoUploading}
                            className="inline-flex items-center px-3 py-1.5 rounded-md border border-red-300 text-red-700 hover:bg-red-50 text-xs disabled:opacity-50"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Remover foto
                          </button>
                        )}

                        {can('patients.update') && <button
                          type="button"
                          onClick={handleRefreshPatientPhoto}
                          disabled={refreshingPatientPhoto}
                          className="inline-flex items-center px-3 py-1.5 rounded-md border border-emerald-300 text-emerald-700 hover:bg-emerald-50 text-xs disabled:opacity-50"
                          title="Atualizar foto enviada pelo paciente"
                        >
                          <RefreshCw
                            className={`w-4 h-4 mr-1 ${
                              refreshingPatientPhoto ? 'animate-spin' : ''
                            }`}
                          />
                          Atualizar foto
                        </button>}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end space-y-2">
                  <div className="flex flex-wrap justify-end gap-2">
                    {can('whatsapp.send') && <button
                      onClick={() => openWhatsAppWeb(selectedPatient)}
                      className="inline-flex items-center px-3 py-1.5 rounded-md bg-green-600 text-white hover:bg-green-700 text-xs"
                    >
                      <MessageCircle className="w-4 h-4 mr-1" />
                      Abrir WhatsApp Web
                    </button>}

                    {canSendOnlineBookingWhatsapp && (
                      <button
                        type="button"
                        onClick={handleSendOnlineBookingLinkWhatsApp}
                        disabled={sendingOnlineBookingWhatsApp}
                        className="inline-flex items-center px-3 py-1.5 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 text-xs disabled:opacity-50"
                        title="Enviar link de autoagendamento por WhatsApp"
                      >
                        <LinkIcon className="w-4 h-4 mr-1" />
                        {sendingOnlineBookingWhatsApp
                          ? 'Enviando...'
                          : 'Enviar autoagendamento por WhatsApp'}
                      </button>
                    )}

                    {can('clinical_records.export') && <button
                      type="button"
                      onClick={() => {
                        const purpose = window.prompt(
                          'Finalidade da exportação:\n\n1 - Uso interno / auditoria\n2 - Cópia solicitada pelo paciente\n3 - Cópia solicitada por responsável/representante\n4 - Encaminhamento para outro profissional\n5 - Solicitação judicial\n6 - Outro\n\nDigite o número:',
                          '1'
                        );

                        if (!purpose) return;

                        const purposeMap = {
                          '1': 'uso_interno_auditoria',
                          '2': 'solicitacao_paciente',
                          '3': 'solicitacao_responsavel_representante',
                          '4': 'encaminhamento_outro_profissional',
                          '5': 'solicitacao_judicial',
                          '6': 'outro',
                        };

                        const exportPurpose = purposeMap[purpose] || 'uso_interno_auditoria';

                        const exportRequester = window.prompt(
                          'Nome do solicitante/recebedor:',
                          selectedPatient?.full_name || ''
                        ) || '';

                        const exportNotes = window.prompt(
                          'Observações sobre a entrega/exportação:',
                          ''
                        ) || '';

                        generateProntuarioPdf({
                          exportPurpose,
                          exportRequester,
                          exportNotes,
                        });
                      }}
                      disabled={exportingProntuario}
                      className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs ${
                        exportingProntuario
                          ? 'bg-indigo-300 text-white cursor-not-allowed'
                          : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }`}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      {exportingProntuario ? 'Gerando prontuário...' : 'Exportar prontuário'}
                    </button>}
                  </div>

                  <div className="flex flex-wrap justify-end gap-2">
                    {can('clinical_records.export') && <button
                      type="button"
                      onClick={loadSnapshotHistory}
                      className="inline-flex items-center px-3 py-1.5 rounded-md border border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-xs"
                      disabled={loadingSnapshots}
                    >
                      <FileText className="w-4 h-4 mr-1" />
                      Histórico PDF
                    </button>}

                    {can('audit.view') && <button
                      type="button"
                      onClick={loadAuditHistory}
                      className="inline-flex items-center px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 text-xs"
                      disabled={loadingAudit}
                    >
                      <ShieldCheck className="w-4 h-4 mr-1" />
                      Auditoria
                    </button>}

                    {can('patients.update') && <button
                      onClick={handleEditPatient}
                      className="inline-flex items-center px-3 py-1.5 rounded-md border border-gray-300 text-xs text-gray-700 hover:bg-gray-50"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar paciente
                    </button>}
                  </div>

                  {can('clinical_records.update') && activeAppt && activeAppt.patient_id === selectedPatient.id && (
                    <div className="mt-2 flex items-center">
                      <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <Calendar className="w-3 h-3 mr-1" />
                        Em atendimento (check-in)
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200">
                <div className="flex flex-wrap border-b">
                  {primaryRecordTabs.map(renderRecordTab)}
                  {additionalRecordTabs.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowAdditionalRecordTabs((current) => !current)}
                      aria-expanded={showAdditionalRecordTabs}
                      className={`-mb-px flex items-center border-b-2 px-4 py-3 text-sm font-medium ${
                        ADDITIONAL_RECORD_TAB_IDS.has(activeTab)
                          ? 'border-purple-600 text-purple-600'
                          : 'border-transparent text-gray-600 hover:text-gray-800'
                      }`}
                    >
                      Mais áreas
                      <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
                        {additionalRecordTabs.length}
                      </span>
                      <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${showAdditionalRecordTabs ? 'rotate-180' : ''}`} />
                    </button>
                  )}
                </div>

                {showAdditionalRecordTabs && additionalRecordTabs.length > 0 && (
                  <div className="flex flex-wrap border-b bg-gray-50 px-2">
                    {additionalRecordTabs.map(renderRecordTab)}
                  </div>
                )}

                <div className="p-4">
                  {activeTab === 'overview' && (
                    <OverviewTab
                      key={(selectedPatient?.id || '') + '-overview'}
                      selectedPatient={selectedPatient}
                      patientData={patientData}
                      formatDate={formatDate}
                      formatCurrency={formatCurrency}
                    />
                  )}

                  {activeTab === 'timeline' && (
                    <div className="space-y-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-800">
                            Timeline do prontuário
                          </h3>
                          <p className="text-xs text-gray-500">
                            Linha do tempo auditável dos principais eventos do paciente.
                          </p>
                        </div>

                        <button
                          onClick={() => loadAuditHistory(false)}
                          className="text-sm px-3 py-1 bg-purple-600 text-white rounded hover:bg-purple-700"
                        >
                          Atualizar
                        </button>
                      </div>

                      {loadingAudit ? (
                        <div className="text-gray-500 text-sm">Carregando eventos...</div>
                      ) : auditHistory.length === 0 ? (
                        <div className="text-gray-500 text-sm">
                          Nenhum evento registrado.
                        </div>
                      ) : (
                        <div className="relative border-l-2 border-gray-200 ml-5">
                          {auditHistory.map((item, index) => {
                            const meta = getTimelineEventMeta(item.action);
                            const Icon = meta.icon;

                            return (
                              <div key={item.id || `${item.action}-${item.created_at}-${index}`} className="mb-6 ml-6 relative">
                                <div className={`absolute -left-[34px] top-2 w-8 h-8 ${meta.dot} rounded-full border-4 border-white flex items-center justify-center shadow`}>
                                  <Icon className="w-4 h-4 text-white" />
                                </div>

                                <div className={`border rounded-lg p-3 shadow-sm ${meta.card}`}>
                                  <div className="flex justify-between items-start gap-3">
                                    <div>
                                      <div className="font-semibold text-gray-900">
                                        {formatAuditAction(item.action)}
                                      </div>
                                      <span className={`inline-flex mt-1 text-[11px] px-2 py-0.5 rounded-full border ${meta.badge}`}>
                                        {meta.label}
                                      </span>
                                    </div>

                                    <div className="text-xs text-gray-500 whitespace-nowrap">
                                      {item.created_at_br || (item.created_at ? formatDateTimeBR(item.created_at) : '—')}
                                    </div>
                                  </div>

                                  <div className="mt-2 text-xs text-gray-600 space-y-1">
                                    {item.ip && (
                                      <div><strong>IP:</strong> {item.ip}</div>
                                    )}

                                    {item.user_agent && (
                                      <div className="break-all">
                                        <strong>Dispositivo:</strong> {item.user_agent}
                                      </div>
                                    )}

                                    {item.details && Object.keys(item.details || {}).length > 0 && (
                                      <div className="mt-2 bg-white/70 border rounded p-2 text-[11px] space-y-1">
                                        {renderAuditDetails(item.details)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'odontogram' && (
                    <div className="bg-gray-50 border rounded-lg p-3">
                      {selectedPatient?.id ? (
                        <OdontogramFacesEmbed
                          patientId={selectedPatient.id}
                          patient={selectedPatient}
                        />
                      ) : (
                        <div className="text-sm text-gray-600">
                          Selecione um paciente para visualizar o odontograma.
                        </div>
                      )}
                    </div>
                  )}

                  {activeTab === 'treatments' && (
                    <TreatmentsTab
                      key={
                        (selectedPatient?.id || '') +
                        '-treatments-' +
                        (dashboardFilter || 'all')
                      }
                      patientData={{ ...selectedPatient, treatments: patientData.treatments }}
                      selectedPatient={selectedPatient}
                      toast={toastCtx?.toast}
                      onDataUpdate={() => selectedPatient?.id && loadPatientBundles(selectedPatient.id)}
                      logAudit={logAudit}
                      initialFilter={dashboardFilter === 'stalled' ? 'stalled' : ''}
                    />
                  )}

                  {hasAlignersModule &&
                    activeTab === 'aligners' && (
                    <AlignersTab
                      key={
                        (selectedPatient?.id ||
                          '') + '-aligners'
                      }
                      selectedPatient={
                        selectedPatient
                      }
                      toast={toastCtx?.toast}
                      logAudit={logAudit}
                      onOpenClinicalEvolution={
                        openClinicalEvolution
                      }
                      focusedAlignerEvent={
                        focusedAlignerEvent
                      }
                      onFocusedAlignerEventHandled={
                        handleFocusedAlignerEventHandled
                      }
                    />
                  )}

                  {activeTab === 'appointments' && (
                    <AppointmentsTab
                      key={(selectedPatient?.id || '') + '-appointments'}
                      patientData={{ ...selectedPatient, appointments: patientData.appointments }}
                      appointmentsLoading={appointmentLoad.patientId !== selectedPatient?.id || appointmentLoad.loading}
                      appointmentLoadError={appointmentLoad.patientId === selectedPatient?.id ? appointmentLoad.error : ''}
                      selectedPatient={selectedPatient}
                      treatments={patientData.treatments}
                      toast={toastCtx?.toast}
                      showToast={toastCtx?.showToast}
                      onDataUpdate={() => selectedPatient?.id && loadPatientBundles(selectedPatient.id)}
                      onAppointmentCompleted={handleAppointmentCompleted}
                    />
                  )}

                  {activeTab === 'analyses' && (
                    <ContextualAIHistory
                      key={(selectedPatient?.id || '') + '-analyses'}
                      patientId={selectedPatient?.id}
                    />
                  )}

                  {activeTab === 'images' && (
                    <ImagesTab
                      key={(selectedPatient?.id || '') + '-images'}
                      patientId={selectedPatient?.id}
                      onOpenImage={(img) => {
                        setSelectedImageForView(img);
                        setShowImageModal(true);
                      }}
                    />
                  )}

                  {activeTab === 'documents' && (
                    <PatientDocumentsTab
                      key={(selectedPatient?.id || '') + '-documents'}
                      selectedPatient={selectedPatient}
                      openContractId={receiptToOpenId}
                    />
                  )}

                  {activeTab === 'anamnese' && (
                    <PatientAnamneseTab
                      key={(selectedPatient?.id || '') + '-anamnese'}
                      patient={selectedPatient}
                      toast={toastCtx}
                      hasWhatsAppModule={hasWhatsAppModuleForActions}
                    />
                  )}

                  {activeTab === 'financial' && (
                    <FinancialTab
                      key={(selectedPatient?.id || '') + '-financial'}
                      selectedPatient={selectedPatient}
                      patientData={patientData}
                      formatCurrency={formatCurrency}
                      formatDate={formatDate}
                      getTotalTreatmentCost={getTotalTreatmentCost}
                      getTotalInvoiceAmount={getTotalInvoiceAmount}
                      getTotalPaidAmount={getTotalPaidAmount}
                      onDataUpdate={() => selectedPatient?.id && loadPatientBundles(selectedPatient.id)}
                    />
                  )}

                  {activeTab === 'evolution' && (
                    <EvolutionTab
                      key={
                        (selectedPatient?.id ||
                          '') + '-evolution'
                      }
                      patientData={patientData}
                      selectedPatient={
                        selectedPatient
                      }
                      treatments={
                        patientData.treatments
                      }
                      toast={toastCtx}
                      onDataUpdate={() =>
                        loadPatientBundles(
                          selectedPatient.id
                        )
                      }
                      logAudit={logAudit}
                      focusedEvolutionId={
                        focusedEvolutionId
                      }
                      onFocusedEvolutionHandled={
                        handleFocusedEvolutionHandled
                      }
                      onOpenAlignerEvent={
                        openAlignerEvent
                      }
                    />
                  )}
                </div>
              </div>

              <PatientForm
                isOpen={pfIsOpen}
                onClose={() => setPfIsOpen(false)}
                onSubmit={handleSavePatient}
                formData={pfFormData}
                setFormData={setPfFormData}
                isEditing={pfIsEditing}
                title={pfIsEditing ? 'Editar Paciente' : 'Novo Paciente'}
                canEditClinicalData={can('clinical_records.update')}
              />

              {showAuditHistory && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                  onClick={() => setShowAuditHistory(false)}
                >
                  <div
                    className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[80vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-4 border-b flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Auditoria do prontuário
                        </h3>
                        <p className="text-xs text-gray-500">
                          Paciente: {selectedPatient?.full_name || '—'}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => setShowAuditHistory(false)}
                        className="text-gray-500 hover:text-gray-800"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="p-4 overflow-y-auto max-h-[65vh]">
                      {auditHistory.length === 0 ? (
                        <div className="text-sm text-gray-500">
                          Nenhum evento de auditoria registrado para este prontuário.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {auditHistory.map((item) => (
                            <div
                              key={item.id || `${item.action}-${item.created_at}`}
                              className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <div className="font-semibold text-gray-900">
                                    {formatAuditAction(item.action)}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Data/hora: {item.created_at_br || (item.created_at ? formatDateTimeBR(item.created_at) : '—')}
                                  </div>
                                </div>

                                <span className="text-xs px-2 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
                                  Audit
                                </span>
                              </div>

                              <div className="mt-2 text-xs text-gray-600 space-y-1">
                                {item.ip && <div><strong>IP:</strong> {item.ip}</div>}
                                {item.user_agent && (
                                  <div className="break-all">
                                    <strong>Navegador/dispositivo:</strong> {item.user_agent}
                                  </div>
                                )}
                                {item.appointment_id && (
                                  <div><strong>Evolução/agendamento:</strong> {item.appointment_id}</div>
                                )}
                                {item.treatment_id && (
                                  <div><strong>Tratamento:</strong> {item.treatment_id}</div>
                                )}
                                {item.details && (
                                  <div className="mt-2 bg-white border rounded p-2 text-[11px] space-y-1">
                                    {renderAuditDetails(item.details)}
                                  </div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {showSnapshotHistory && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
                  onClick={() => setShowSnapshotHistory(false)}
                >
                  <div
                    className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[80vh] overflow-hidden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="p-4 border-b flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          Histórico de versões do prontuário
                        </h3>
                        <p className="text-xs text-gray-500">
                          Paciente: {selectedPatient?.full_name || '—'}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => setShowSnapshotHistory(false)}
                        className="text-gray-500 hover:text-gray-800"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="p-4 overflow-y-auto max-h-[65vh]">
                      {snapshotHistory.length === 0 ? (
                        <div className="text-sm text-gray-500">
                          Nenhuma versão de PDF registrada para este prontuário.
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {snapshotHistory.map((item) => (
                            <div
                              key={item.id}
                              className="border border-gray-200 rounded-lg p-3 bg-gray-50"
                            >
                              <div className="flex items-center justify-between gap-3">
                                <div>
                                  <div className="font-semibold text-gray-900">
                                    Versão {item.version}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Gerado em: {item.created_at_br || (item.created_at ? formatDateTimeBR(item.created_at) : '—')}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    Arquivo: {item.file_name || '—'}
                                  </div>
                                </div>

                                <div className="flex items-center gap-2">
                                  <span className="text-xs px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
                                    Snapshot
                                  </span>

                                  <button
                                    type="button"
                                    onClick={() => downloadSnapshotPdf(item.id)}
                                    className="text-xs px-2 py-1 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
                                  >
                                    Baixar PDF
                                  </button>
                                </div>
                              </div>

                              <div className="mt-2 text-xs text-gray-600 break-all">
                                <strong>Hash SHA-256:</strong> {item.hash_sha256 || '—'}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {showImageModal && selectedImageForView && (
                <ImageModal
                  image={selectedImageForView}
                  onClose={() => {
                    setShowImageModal(false);
                    setSelectedImageForView(null);
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}

      {mergeSourcePatient && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4"
          onClick={() => closeMergePatient()}
        >
          <div
            className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-gray-200 p-5">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Mesclar prontuários duplicados</h2>
                <p className="mt-1 text-sm text-gray-500">
                  Cadastro que deixará de aparecer: <strong>{mergeSourcePatient.full_name}</strong>
                </p>
              </div>
              <button
                type="button"
                onClick={() => closeMergePatient()}
                disabled={mergingPatient}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                aria-label="Fechar"
              >
                <XCircle className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 p-5">
              <div>
                <label className="block text-sm font-semibold text-gray-800">
                  Localize o prontuário correto que será mantido
                </label>
                <input
                  type="text"
                  value={mergeTargetSearch}
                  onChange={(event) => {
                    setMergeTargetSearch(event.target.value);
                    setMergeTargetPatient(null);
                    setMergePreview(null);
                    setMergeFieldChoices({});
                    setMergeIdentityConfirmed(false);
                  }}
                  placeholder="Digite pelo menos duas letras do nome, CPF ou telefone"
                  className="mt-2 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
                />
              </div>

              {!mergeTargetPatient && mergeTargetCandidates.length > 0 && (
                <div className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
                  {mergeTargetCandidates.map((patient) => (
                    <button
                      key={patient.id}
                      type="button"
                      onClick={() => {
                        setMergeTargetPatient(patient);
                      setMergeTargetSearch(patient.full_name || '');
                      setMergeFieldChoices({});
                      setMergeIdentityConfirmed(false);
                      }}
                      className="block w-full px-4 py-3 text-left hover:bg-purple-50"
                    >
                      <span className="block text-sm font-semibold text-gray-900">{patient.full_name}</span>
                      <span className="mt-1 block text-xs text-gray-500">
                        CPF: {patient.cpf ? formatCpfDisplay(patient.cpf) : 'não informado'} · Telefone: {formatPhoneDisplay(patient.phone)}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {mergeTargetPatient && (
                <div className="rounded-xl border border-purple-200 bg-purple-50 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-purple-600">Prontuário que será mantido</p>
                  <p className="mt-1 font-bold text-purple-900">{mergeTargetPatient.full_name}</p>
                  <button
                    type="button"
                    onClick={() => {
                      setMergeTargetPatient(null);
                      setMergeTargetSearch('');
                      setMergePreview(null);
                      setMergeFieldChoices({});
                      setMergeIdentityConfirmed(false);
                    }}
                    className="mt-2 text-xs font-semibold text-purple-700 hover:text-purple-900"
                  >
                    Escolher outro
                  </button>
                </div>
              )}

              {loadingMergePreview && (
                <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  Conferindo os dados e históricos dos prontuários...
                </div>
              )}

              {mergePreview && !loadingMergePreview && (
                mergePreview.can_merge ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                    <p className="font-semibold">Conferência concluída.</p>
                    <p className="mt-1">
                      Os registros do cadastro duplicado serão transferidos para o prontuário principal. Campos que estiverem vazios também serão aproveitados.
                    </p>
                    {mergePreview.requires_history_transfer && (
                      <p className="mt-2 font-medium">
                        Há histórico, agendamento ou tratamento vinculado. Essas informações serão preservadas no prontuário mantido.
                      </p>
                    )}
                    {mergePreview.empty_planned_aligner_treatments?.length > 0 && (
                      <p className="mt-2">
                        O tratamento de alinhadores apenas planejado e sem acompanhamentos será inativado para não continuar aparecendo em duplicidade.
                      </p>
                    )}
                  </div>
                ) : null
              )}

              {mergePreview?.can_merge && mergeConflicts.length > 0 && !loadingMergePreview && (
                <section className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                  <h3 className="font-semibold text-amber-900">
                    Escolha quais informações serão preservadas
                  </h3>
                  <p className="mt-1 text-sm text-amber-800">
                    Só perguntamos porque os dois cadastros possuem valores diferentes nestes campos.
                  </p>

                  <div className="mt-4 space-y-4">
                    {mergeConflicts.map((conflict) => (
                      <fieldset key={conflict.field} className="rounded-xl border border-amber-200 bg-white p-3">
                        <legend className="px-1 text-sm font-bold text-gray-900">{conflict.label}</legend>
                        <div className="mt-2 grid gap-2">
                          {[
                            ['target', mergeTargetPatient?.full_name, conflict.target_value],
                            ['source', mergeSourcePatient?.full_name, conflict.source_value],
                          ].map(([choice, patientName, value]) => (
                            <label key={choice} className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 ${mergeFieldChoices[conflict.field] === choice ? 'border-purple-400 bg-purple-50' : 'border-gray-200'}`}>
                              <input
                                type="radio"
                                name={`merge-${conflict.field}`}
                                value={choice}
                                checked={mergeFieldChoices[conflict.field] === choice}
                                onChange={() => setMergeFieldChoices((current) => ({ ...current, [conflict.field]: choice }))}
                                className="mt-1 h-4 w-4"
                              />
                              <span className="min-w-0">
                                <span className="block text-xs font-semibold text-gray-500">Cadastro: {patientName}</span>
                                <span className="mt-1 block break-words text-sm font-medium text-gray-900">
                                  {formatMergeFieldValue(conflict.field, value)}
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </fieldset>
                    ))}
                  </div>
                </section>
              )}

              {mergePreview?.can_merge && mergeConflicts.length === 0 && !loadingMergePreview && (
                <p className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">
                  Não há informações divergentes. Os dados existentes em apenas um cadastro serão aproveitados automaticamente.
                </p>
              )}

              {mergePreview?.can_merge && !loadingMergePreview && (
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900">
                  <input
                    type="checkbox"
                    checked={mergeIdentityConfirmed}
                    onChange={(event) => setMergeIdentityConfirmed(event.target.checked)}
                    className="mt-0.5 h-4 w-4"
                  />
                  <span>
                    Confirmo que <strong>{mergeSourcePatient.full_name}</strong> e <strong>{mergeTargetPatient.full_name}</strong> são a mesma pessoa.
                  </span>
                </label>
              )}
            </div>

            <div className="flex flex-col-reverse gap-2 border-t border-gray-200 p-5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => closeMergePatient()}
                disabled={mergingPatient}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleMergeEmptyPatient}
                disabled={!mergePreview?.can_merge || !mergeIdentityConfirmed || !allMergeConflictsResolved || loadingMergePreview || mergingPatient}
                className="inline-flex items-center justify-center rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <GitMerge className="mr-2 h-4 w-4" />
                {mergingPatient ? 'Mesclando...' : 'Confirmar mesclagem'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
